/**
 * Visual Template Editor Page
 *
 * Main page component that orchestrates the inline visual editor.
 * Loads a template JSON, renders it with EditableRenderer,
 * manages state changes, and saves back to the server.
 */

import React, { useState, useEffect, useCallback, useRef, useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';
import AuthContext from '../context/AuthContext';
import API_BASE_URL from '../config/api';
import { devLog } from '../utils/devLog';
import { ArrowLeft, Sparkles } from 'lucide-react';
import EditableRenderer from '../components/VisualEditor/EditableRenderer';
import EditorTopBar from '../components/VisualEditor/EditorTopBar';
import ThemePanel from '../components/VisualEditor/ThemePanel';
import BusinessInfoPanel from '../components/VisualEditor/BusinessInfoPanel';
import SectionLibrary from '../components/VisualEditor/SectionLibrary';
import VersionHistory from '../components/VisualEditor/VersionHistory';
import FloatingToolbar from '../components/VisualEditor/FloatingToolbar';
import BreadcrumbBar from '../components/VisualEditor/BreadcrumbBar';
import PropertiesPanel from '../components/VisualEditor/PropertiesPanel';
import AnimationPanel from '../components/VisualEditor/AnimationPanel';
import OnboardingTour from '../components/VisualEditor/OnboardingTour';
import ProductPickerPanel from '../components/VisualEditor/ProductPickerPanel';
import NavbarConfigPanel from '../components/VisualEditor/previews/NavbarConfigPanel';
import FooterConfigPanel from '../components/VisualEditor/previews/FooterConfigPanel';
import { UndoRedoManager } from '../components/VisualEditor/UndoRedoManager';
import { updateContentByBinding, deepClone, updateNodeStyle, findNodeByPath } from '../components/VisualEditor/bindingUtils';

// Task 4.17: Configurable customer site base URL (replaces hardcoded localhost:8002)
const CUSTOMER_SITE_BASE = process.env.REACT_APP_CUSTOMER_SITE_URL || 'localhost:8002';

// Recursively find a node by component type and update its attributes
function updateBlockAttributes(node, componentType, newAttributes) {
  if (!node || typeof node !== 'object') return false;
  if (node.type === componentType) {
    node.attributes = { ...node.attributes, ...newAttributes };
    return true;
  }
  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      if (updateBlockAttributes(child, componentType, newAttributes)) return true;
    }
  }
  return false;
}

// Recursively find a node by component type and return its attributes
function findComponentAttributes(templateJSON, componentType) {
  if (!templateJSON?.blocks) return {};
  for (const block of templateJSON.blocks) {
    const attrs = findNodeAttributes(block.structure, componentType);
    if (attrs) return attrs;
  }
  return {};
}

function findNodeAttributes(node, componentType) {
  if (!node || typeof node !== 'object') return null;
  if (node.type === componentType) return node.attributes || {};
  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      const found = findNodeAttributes(child, componentType);
      if (found) return found;
    }
  }
  return null;
}

const VisualTemplateEditor = () => {
  const { authToken, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const accessToken = authToken?.access;

  const templateNo = searchParams.get('template') ? parseInt(searchParams.get('template'), 10) : null;
  const pageType = searchParams.get('page') || 'main';

  // State
  const [templateJSON, setTemplateJSON] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false); // Task 4.5: ref-based guard to prevent race conditions
  const [isDirty, setIsDirty] = useState(false);
  const [editMode, setEditMode] = useState(true);
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [showThemePanel, setShowThemePanel] = useState(false);
  const [showBusinessPanel, setShowBusinessPanel] = useState(false);
  const [showSectionLibrary, setShowSectionLibrary] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [undoInfo, setUndoInfo] = useState({ canUndo: false, canRedo: false });
  const [undoToast, setUndoToast] = useState(null);
  const [pendingFiles, setPendingFiles] = useState({});
  const [templateName, setTemplateName] = useState('');
  const [selectedElement, setSelectedElement] = useState(null);
  const [editingNodePath, setEditingNodePath] = useState(null);
  const [showPropertiesPanel, setShowPropertiesPanel] = useState(false);
  const [hintsDismissed, setHintsDismissed] = useState(!!localStorage.getItem('editor_hints_dismissed'));
  const [autoSaveStatus, setAutoSaveStatus] = useState(null); // null | 'saving' | 'saved'
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [activeConfigPanel, setActiveConfigPanel] = useState(null); // null | 'navbar' | 'footer'
  const [zoomLevel, setZoomLevel] = useState(100); // Canvas zoom (25–200%)
  const [showAnimationPanel, setShowAnimationPanel] = useState(false);
  const [copiedStyle, setCopiedStyle] = useState(null); // For copy/paste style feature
  const [navbarAttrs, setNavbarAttrs] = useState(null); // Persistent navbar attributes for wrapping pages
  const [footerAttrs, setFooterAttrs] = useState(null); // Persistent footer attributes for wrapping pages
  const [designerProducts, setDesignerProducts] = useState([]); // Real products from designer's catalog
  const [designerCategories, setDesignerCategories] = useState([]); // Real categories from designer's products
  const [designerCollections, setDesignerCollections] = useState([]); // Real collections from designer
  const [showProductPicker, setShowProductPicker] = useState(false); // Product picker panel visibility

  const undoManager = useRef(null);
  const canvasRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  // Master theme persists across page switches — ensures consistent theme on all pages
  const masterThemeRef = useRef(null);
  // Track if navbar/footer attrs were modified from a non-layout page
  const navbarDirtyRef = useRef(false);
  const footerDirtyRef = useRef(false);
  // Track if placeholder product data has been replaced with real data (run only once)
  const productDataReplacedRef = useRef(false);
  // Ref to access handleSave from handlePageChange without hook ordering issues
  const handleSaveRef = useRef(null);

  // Helper: normalize v1.0 single-structure templates (navbar/footer) into v2.0 blocks format
  const normalizeTemplateJSON = useCallback((data, type) => {
    if (data && data.blocks && data.blocks.length > 0) return data; // already v2.0
    if (data && data.structure) {
      // v1.0 format: single structure at top level → wrap into blocks
      return {
        version: '2.0',
        page_type: type || data.component_type || 'navbar',
        template_id: data.template_id || templateNo,
        template_name: data.template_name || `Template ${templateNo}`,
        theme: data.theme || {},
        content: data.content || {},
        blocks: [{
          id: data.component_type || type || 'main-block',
          type: 'section',
          position: 1,
          enabled: true,
          visible_on: ['desktop', 'tablet', 'mobile'],
          structure: data.structure,
        }],
      };
    }
    return data;
  }, [templateNo]);

  // Helper: create empty page JSON for any page type
  const createEmptyPageJSON = useCallback((type) => ({
    version: '2.0',
    page_type: type,
    template_id: templateNo,
    template_name: `Template ${templateNo}`,
    blocks: [],
    content: {},
    theme: {}
  }), [templateNo]);

  // Helper: try recovering a local draft
  const tryRecoverDraft = useCallback((currentJSON) => {
    try {
      const draftKey = `editor_draft_${templateNo}_${pageType}`;
      const draftRaw = localStorage.getItem(draftKey);
      if (draftRaw) {
        const draft = JSON.parse(draftRaw);
        if (draft.timestamp && (Date.now() - draft.timestamp) < 86400000) {
          const draftStr = JSON.stringify(draft.json?.content);
          const currentStr = JSON.stringify(currentJSON?.content);
          if (draftStr !== currentStr) {
            Swal.fire({
              title: 'Recover Unsaved Changes?',
              text: 'Found a local draft from ' + new Date(draft.timestamp).toLocaleString() + '. Would you like to restore it?',
              icon: 'question',
              showCancelButton: true,
              confirmButtonText: 'Restore Draft',
              cancelButtonText: 'Discard Draft',
              confirmButtonColor: '#3b82f6',
            }).then((result) => {
              if (result.isConfirmed && draft.json) {
                setTemplateJSON(draft.json);
                setIsDirty(true);
                undoManager.current?.push(draft.json, 'Restore local draft');
              }
              localStorage.removeItem(draftKey);
            });
          }
        }
      }
    } catch (e) {
      // Ignore draft recovery errors
    }
  }, [templateNo, pageType]);

  // Initialize template state from loaded data
  // Theme propagation: main page's theme is the single source of truth.
  // Non-main pages always inherit the master theme (overrides base template themes).
  const initializeTemplate = useCallback((data, name, isMainPage) => {
    const hasTheme = data?.theme?.colors && Object.keys(data.theme.colors).length > 0;

    if (isMainPage && hasTheme) {
      // Main page owns the theme — cache it as master
      masterThemeRef.current = JSON.parse(JSON.stringify(data.theme));
    } else if (!isMainPage && masterThemeRef.current) {
      // Non-main page — always use master theme for consistency
      data = { ...data, theme: JSON.parse(JSON.stringify(masterThemeRef.current)) };
    } else if (!isMainPage && hasTheme && !masterThemeRef.current) {
      // Non-main page loaded directly with no master yet — use its own theme as fallback
      masterThemeRef.current = JSON.parse(JSON.stringify(data.theme));
    }

    setTemplateJSON(data);
    setTemplateName(name);
    undoManager.current = new UndoRedoManager(data);
    undoManager.current.subscribe(setUndoInfo);
    setIsDirty(false);
    tryRecoverDraft(data);
  }, [tryRecoverDraft]);

  // Load template JSON from server
  useEffect(() => {
    if (!accessToken || !templateNo) {
      setIsLoading(false);
      return;
    }

    // Reset product data replacement flag so real data is injected for the new page
    productDataReplacedRef.current = false;

    const loadTemplate = async () => {
      setIsLoading(true);

      // For non-main pages, ensure we have the master theme from the main page
      if (pageType !== 'main' && !masterThemeRef.current) {
        try {
          const mainResp = await axios.get(
            `${API_BASE_URL}/api/template/${templateNo}/page/main`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );
          const mainData = mainResp.data?.data || mainResp.data;
          if (mainData?.theme?.colors && Object.keys(mainData.theme.colors).length > 0) {
            masterThemeRef.current = JSON.parse(JSON.stringify(mainData.theme));
          }
        } catch {
          // Ignore — initializeTemplate will handle missing theme
        }
      }

      // Helper: load base template from static files
      const fetchBaseTemplate = async (type) => {
        const pathMap = {
          'main': `template_${templateNo}.json`,
          'about': `about/about${templateNo}.json`,
          'shop': `shop/shop${templateNo}.json`,
          'product': `product/product${templateNo}.json`,
          'contact': `contact/contact${templateNo}.json`,
          'login': `login/login${templateNo}.json`,
          'cart': `cart/cart${templateNo}.json`,
          'checkout': `checkout/checkout${templateNo}.json`,
          'signup': `signup/signup${templateNo}.json`,
          'navbar': `navbar/navbar${templateNo}.json`,
          'footer': `footer/footer${templateNo}.json`,
          'privacy': `privacy/privacy${templateNo}.json`,
          'shipping': `shipping/shipping${templateNo}.json`,
          'terms': `terms/terms${templateNo}.json`,
          'faqs': `faqs/faqs${templateNo}.json`,
          'categories': `categories/categories${templateNo}.json`,
          'collections': `collections/collections${templateNo}.json`,
        };
        const path = pathMap[type || 'main'] || pathMap['main'];
        try {
          return (await axios.get(`/static/template_bases/${path}`)).data;
        } catch {
          return (await axios.get(`${API_BASE_URL}/static/template_bases/${path}`)).data;
        }
      };

      // Load navbar/footer for persistent display (skip when editing them standalone)
      const isLayoutPage = pageType === 'navbar' || pageType === 'footer';
      if (!isLayoutPage) {
        const extractAttrs = (data) => {
          // v1.0: data.structure.attributes, v2.0: data.blocks[0].structure.attributes
          if (data?.structure?.attributes) return data.structure.attributes;
          if (data?.blocks?.[0]?.structure?.attributes) return data.blocks[0].structure.attributes;
          return null;
        };
        // Load navbar
        try {
          const navResp = await axios.get(
            `${API_BASE_URL}/api/template/${templateNo}/page/navbar`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );
          const navData = navResp.data?.data || navResp.data;
          let attrs = extractAttrs(navData);
          if (!attrs) {
            const baseNav = await fetchBaseTemplate('navbar');
            attrs = extractAttrs(baseNav);
          }
          if (attrs) setNavbarAttrs(attrs);
        } catch {
          try {
            const baseNav = await fetchBaseTemplate('navbar');
            const attrs = extractAttrs(baseNav);
            if (attrs) setNavbarAttrs(attrs);
          } catch { /* no navbar available */ }
        }
        // Load footer
        try {
          const footResp = await axios.get(
            `${API_BASE_URL}/api/template/${templateNo}/page/footer`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );
          const footData = footResp.data?.data || footResp.data;
          let attrs = extractAttrs(footData);
          if (!attrs) {
            const baseFoot = await fetchBaseTemplate('footer');
            attrs = extractAttrs(baseFoot);
          }
          if (attrs) setFooterAttrs(attrs);
        } catch {
          try {
            const baseFoot = await fetchBaseTemplate('footer');
            const attrs = extractAttrs(baseFoot);
            if (attrs) setFooterAttrs(attrs);
          } catch { /* no footer available */ }
        }
      } else {
        setNavbarAttrs(null);
        setFooterAttrs(null);
      }

      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/template/${templateNo}/page/${pageType}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );

        const responseData = response.data;
        const templateData = responseData.data || responseData;

        // Check if blocks are actually renderable (have structure HTML)
        const hasValidBlocks = templateData?.blocks?.length > 0 &&
          templateData.blocks.some(b => b.structure);

        const isMain = pageType === 'main';

        if (templateData && templateData.blocks && hasValidBlocks) {
          // Saved data has valid blocks — use it directly
          initializeTemplate(
            templateData,
            templateData.template_name || responseData.temp_name || `Template ${templateNo}`,
            isMain
          );
        } else {
          // No valid saved data — load fresh from static base template
          try {
            const rawJSON = await fetchBaseTemplate(pageType);
            const baseJSON = normalizeTemplateJSON(rawJSON, pageType);
            if (baseJSON && baseJSON.blocks && baseJSON.blocks.length > 0) {
              initializeTemplate(baseJSON, baseJSON.template_name || `Template ${templateNo}${!isMain ? ' — ' + pageType : ''}`, isMain);
            } else {
              const emptyPage = createEmptyPageJSON(pageType);
              initializeTemplate(emptyPage, `Template ${templateNo} — ${pageType}`, false);
              if (!isMain) setShowSectionLibrary(true);
            }
          } catch {
            const emptyPage = createEmptyPageJSON(pageType);
            initializeTemplate(emptyPage, `Template ${templateNo} — ${pageType}`, false);
            if (!isMain) setShowSectionLibrary(true);
          }
        }
      } catch (err) {
        devLog.error('Failed to load saved template:', err);
        // Try loading base template for this page type
        try {
          const rawJSON = await fetchBaseTemplate(pageType);
          const baseJSON = normalizeTemplateJSON(rawJSON, pageType);
          if (baseJSON && baseJSON.blocks && baseJSON.blocks.length > 0) {
            initializeTemplate(baseJSON, baseJSON.template_name || `Template ${templateNo}`, pageType === 'main');
          } else {
            const emptyPage = createEmptyPageJSON(pageType);
            initializeTemplate(emptyPage, `Template ${templateNo} — ${pageType}`, pageType === 'main');
            if (pageType !== 'main') setShowSectionLibrary(true);
          }
        } catch (baseErr) {
          devLog.error('Failed to load base template:', baseErr);
          if (pageType === 'main') {
            Swal.fire('Error', 'Could not load template. Please try again.', 'error');
            navigate('/template-builder/' + templateNo);
          } else {
            const emptyPage = createEmptyPageJSON(pageType);
            initializeTemplate(emptyPage, `Template ${templateNo} — ${pageType}`, false);
            setShowSectionLibrary(true);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadTemplate();
  }, [accessToken, templateNo, pageType, navigate, createEmptyPageJSON, initializeTemplate]);

  // ── Fetch designer's real products, collections, and categories ──
  useEffect(() => {
    if (!accessToken) return;

    const fetchDesignerData = async () => {
      // Fetch products
      try {
        const prodRes = await axios.get(`${API_BASE_URL}/api/products/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (prodRes.data) {
          const products = Array.isArray(prodRes.data)
            ? prodRes.data
            : (prodRes.data.products || []);
          setDesignerProducts(products);
        }
      } catch (err) {
        devLog.error('Failed to fetch designer products:', err);
      }

      // Fetch collections
      try {
        const collRes = await axios.get(`${API_BASE_URL}/api/collections/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (collRes.data?.collections) {
          setDesignerCollections(collRes.data.collections);
        }
      } catch (err) {
        // Collections may not exist — silently ignore
      }

      // Fetch categories
      try {
        const catRes = await axios.get(`${API_BASE_URL}/api/categories/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (catRes.data?.categories) {
          setDesignerCategories(catRes.data.categories);
        } else if (Array.isArray(catRes.data)) {
          setDesignerCategories(catRes.data);
        }
      } catch (err) {
        // Categories may not exist — silently ignore
      }
    };

    fetchDesignerData();
  }, [accessToken]);

  // ── Replace placeholder product/collection data with real designer data (once only) ──
  useEffect(() => {
    if (!templateJSON || designerProducts.length === 0 || productDataReplacedRef.current) return;
    productDataReplacedRef.current = true;

    const mapProduct = (p) => {
      const resolveImg = (img) => img
        ? (typeof img === 'string' && img.startsWith('http') ? img : `${API_BASE_URL}${img}`)
        : '';
      const price = Number(p.Discountedprice || p.price || 0);
      const originalPrice = p.Discountedprice && p.price ? Number(p.price) : null;
      const discountPct = p.Discountpercent ? Math.round(Number(p.Discountpercent)) : null;

      return {
        id: p.id,
        name: p.name,
        price: `\u20b9${price.toLocaleString('en-IN')}`,
        originalPrice: originalPrice ? `\u20b9${originalPrice.toLocaleString('en-IN')}` : null,
        Discountedprice: p.Discountedprice,
        Discountpercent: discountPct,
        category: p.category || '',
        fabric: p.fabric || '',
        color: p.color || '',
        gender: p.gender || '',
        description: p.description || '',
        image: resolveImg(p.img1),
        img1: resolveImg(p.img1),
        img2: resolveImg(p.img2),
        img3: resolveImg(p.img3),
        img4: resolveImg(p.img4),
        in_stock: p.in_stock !== false,
        displayPrice: `\u20b9${price.toLocaleString('en-IN')}`,
      };
    };

    setTemplateJSON(prev => {
      if (!prev) return prev;
      const newJSON = JSON.parse(JSON.stringify(prev));
      if (!newJSON.content) newJSON.content = {};
      const content = newJSON.content;
      const maxItems = 8;
      const mapped = designerProducts.slice(0, maxItems).map(mapProduct);

      // Helper: check if a product section contains only placeholder data (no real IDs or pexels images)
      const isPlaceholder = (items) => {
        if (!items || items.length === 0) return true;
        // If items have numeric IDs from 1-100 (placeholder range) and stock images, treat as placeholder
        // Task 4.18: Added placehold.co to stock image URL detection
        return items.every(item => {
          const hasPlaceholderImage = typeof item.image === 'string' &&
            (item.image.includes('pexels.com') || item.image.includes('unsplash.com') ||
             item.image.includes('placeholder') || item.image.includes('placehold.co') ||
             item.image.includes('via.placeholder.com') || item.image.includes('picsum.photos'));
          const hasSmallId = typeof item.id === 'number' && item.id <= 100;
          return hasPlaceholderImage || hasSmallId || !item.id;
        });
      };

      // Replace products (dict format: { title, items } or array format)
      if (content.products) {
        if (Array.isArray(content.products) && isPlaceholder(content.products)) {
          content.products = mapped;
        } else if (content.products.items && isPlaceholder(content.products.items)) {
          content.products.items = mapped;
        }
      }

      // Replace featured_products, new_arrivals, bestsellers, etc.
      ['featured_products', 'new_arrivals', 'featuredProducts', 'bestsellers'].forEach(key => {
        if (content[key]) {
          if (Array.isArray(content[key]) && isPlaceholder(content[key])) {
            content[key] = mapped;
          } else if (content[key].items && isPlaceholder(content[key].items)) {
            content[key].items = mapped;
          }
        }
      });

      // Replace categories with real data if available
      if (designerCategories.length > 0 && content.categories) {
        const catItems = Array.isArray(content.categories) ? content.categories : (content.categories.items || []);
        if (isPlaceholder(catItems)) {
          const mappedCats = designerCategories.slice(0, 6).map(cat => ({
            id: cat.name,
            name: cat.name,
            image: cat.display_image || '',
            product_count: cat.product_count || 0,
          }));
          if (Array.isArray(content.categories)) {
            content.categories = mappedCats;
          } else if (content.categories.items) {
            content.categories.items = mappedCats;
          }
        }
      }

      // Replace collections with real data if available
      if (designerCollections.length > 0 && content.collections) {
        const collItems = Array.isArray(content.collections) ? content.collections : (content.collections.items || []);
        if (isPlaceholder(collItems)) {
          const mappedColls = designerCollections.slice(0, 6).map(coll => ({
            id: coll.id,
            name: coll.name,
            image: coll.image || '',
            product_count: coll.product_count || 0,
            slug: coll.slug || '',
          }));
          if (Array.isArray(content.collections)) {
            content.collections = mappedColls;
          } else if (content.collections.items) {
            content.collections.items = mappedColls;
          }
        }
      }

      // For shop page — replace placeholder products AND categories with real data
      if (pageType === 'shop') {
        const shopMapped = designerProducts.slice(0, 24).map(mapProduct);
        // Ensure products exist in content at ALL possible data paths
        content.products = shopMapped;
        content.featured_products = shopMapped;
        content.all_products = shopMapped;
        if (content.products?.items) content.products.items = shopMapped;

        // Also replace category filter buttons with real categories
        if (designerCategories.length > 0) {
          content.categories = designerCategories.map(c => ({
            name: c.name,
            count: c.product_count,
            product_count: c.product_count,
          }));
        }
      }

      // For product page — show first real product instead of placeholder
      if (pageType === 'product') {
        const fp = designerProducts[0];
        const resolveImg = (img) => img
          ? (typeof img === 'string' && img.startsWith('http') ? img : `${API_BASE_URL}${img}`)
          : '';
        content.product = {
          id: fp.id,
          name: fp.name,
          price: fp.Discountedprice
            ? `\u20b9${Number(fp.Discountedprice).toLocaleString('en-IN')}`
            : `\u20b9${Number(fp.price || 0).toLocaleString('en-IN')}`,
          originalPrice: fp.Discountedprice && fp.price
            ? `\u20b9${Number(fp.price).toLocaleString('en-IN')}`
            : null,
          Discountedprice: fp.Discountedprice,
          Discountpercent: fp.Discountpercent,
          description: fp.description || 'Product description',
          category: fp.category || '',
          fabric: fp.fabric || '',
          color: fp.color || '',
          gender: fp.gender || '',
          img1: resolveImg(fp.img1),
          img2: resolveImg(fp.img2),
          img3: resolveImg(fp.img3),
          img4: resolveImg(fp.img4),
          image: resolveImg(fp.img1),
          images: [fp.img1, fp.img2, fp.img3, fp.img4].filter(Boolean).map(resolveImg),
          in_stock: fp.in_stock !== false,
        };
      }

      return newJSON;
    });
  }, [templateJSON, designerProducts, designerCategories, designerCollections, pageType]);

  // Warn on tab close with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Save draft to localStorage (debounced 2s)
  useEffect(() => {
    if (!templateJSON || !templateNo) return;
    const timer = setTimeout(() => {
      try {
        const key = `editor_draft_${templateNo}_${pageType}`;
        localStorage.setItem(key, JSON.stringify({
          json: templateJSON,
          timestamp: Date.now(),
          templateNo,
          pageType
        }));
      } catch (e) {
        // Task 4.11: Handle QuotaExceededError gracefully
        if (e?.name === 'QuotaExceededError' || e?.code === 22 || e?.code === 1014) {
          // Try clearing old drafts from other templates/pages
          try {
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
              const k = localStorage.key(i);
              if (k && k.startsWith('editor_draft_') && k !== `editor_draft_${templateNo}_${pageType}`) {
                keysToRemove.push(k);
              }
            }
            keysToRemove.forEach(k => localStorage.removeItem(k));
            // Retry once after cleanup
            localStorage.setItem(`editor_draft_${templateNo}_${pageType}`, JSON.stringify({
              json: templateJSON, timestamp: Date.now(), templateNo, pageType
            }));
          } catch (retryErr) {
            // Show toast about storage full
            setAutoSaveStatus(null);
            const toast = document.createElement('div');
            toast.innerHTML = '<div style="position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#7c2d12;color:white;padding:10px 20px;border-radius:10px;font-size:13px;font-weight:500;z-index:99999;box-shadow:0 4px 12px rgba(0,0,0,0.2)">Browser storage full — local drafts disabled. Your work is still auto-saved to the server.</div>';
            document.body.appendChild(toast);
            setTimeout(() => toast.remove(), 4000);
          }
        }
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [templateJSON, templateNo, pageType]);

  // Handle text content changes
  const handleContentChange = useCallback((bindingPath, newValue, nodePath) => {
    setTemplateJSON(prev => {
      if (!prev) return prev;
      const newJSON = deepClone(prev);

      // Helper to navigate to a node by dot-separated path
      const navigateToNode = (obj, pathStr) => {
        const parts = pathStr.split('.');
        let node = obj;
        for (const part of parts) {
          if (node == null) return null;
          if (Array.isArray(node)) node = node[parseInt(part, 10)];
          else node = node[part];
        }
        return node;
      };

      if (bindingPath && bindingPath.startsWith('__inlineLoop:')) {
        // Format 3 inline loop items — data lives in the JSON structure
        const inlinePath = bindingPath.slice('__inlineLoop:'.length);
        const parts = inlinePath.split('.');
        const parentPath = parts.slice(0, -1).join('.');
        const lastKey = parts[parts.length - 1];
        const parent = navigateToNode(newJSON, parentPath);
        if (parent != null) {
          if (Array.isArray(parent)) {
            parent[parseInt(lastKey, 10)] = newValue;
          } else {
            parent[lastKey] = newValue;
          }
        }
      } else if (bindingPath) {
        newJSON.content = updateContentByBinding(bindingPath, newValue, newJSON.content);
      } else if (nodePath) {
        const node = navigateToNode(newJSON, nodePath);
        if (node) node.content = newValue;
      }

      undoManager.current?.push(newJSON, `Edit text: ${bindingPath || nodePath}`);
      return newJSON;
    });
    setIsDirty(true);
  }, []);

  // Handle image replacement
  const handleImageReplace = useCallback((bindingPath, file, nodePath) => {
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setTemplateJSON(prev => {
      if (!prev) return prev;
      const newJSON = deepClone(prev);

      // Helper to navigate to a node by dot-separated path
      const navigateToNode = (obj, pathStr) => {
        const parts = pathStr.split('.');
        let node = obj;
        for (const part of parts) {
          if (node == null) return null;
          if (Array.isArray(node)) node = node[parseInt(part, 10)];
          else node = node[part];
        }
        return node;
      };

      if (bindingPath && bindingPath.startsWith('__bgImage:')) {
        // Background image replacement — update style.backgroundImage in the JSON structure
        const bgNodePath = bindingPath.slice('__bgImage:'.length);
        const node = navigateToNode(newJSON, bgNodePath);
        if (node) {
          if (!node.style) node.style = {};
          node.style.backgroundImage = `url(${previewUrl})`;
        }
        setPendingFiles(pf => ({ ...pf, [bgNodePath + '.style.backgroundImage']: file }));
      } else if (bindingPath && bindingPath.startsWith('__inlineLoop:')) {
        // Format 3 inline loop items — data lives in the JSON structure's loop.items array
        const inlinePath = bindingPath.slice('__inlineLoop:'.length);
        // Navigate to parent and set the leaf key (handles both existing and missing values)
        const parts = inlinePath.split('.');
        const parentPath = parts.slice(0, -1).join('.');
        const lastKey = parts[parts.length - 1];
        const parent = navigateToNode(newJSON, parentPath);
        if (parent != null) {
          if (Array.isArray(parent)) {
            parent[parseInt(lastKey, 10)] = previewUrl;
          } else {
            parent[lastKey] = previewUrl;
          }
        }
        setPendingFiles(pf => ({ ...pf, [inlinePath]: file }));
      } else if (bindingPath) {
        // Standard content binding — update content object
        newJSON.content = updateContentByBinding(bindingPath, previewUrl, newJSON.content);
        setPendingFiles(pf => ({ ...pf, [bindingPath]: file }));
      } else if (nodePath) {
        // Fallback: update attributes.src or style.backgroundImage at the node path
        const node = navigateToNode(newJSON, nodePath);
        if (node) {
          if (node.attributes && (node.type === 'img' || node.attributes.src !== undefined)) {
            node.attributes.src = previewUrl;
          } else if (node.style?.backgroundImage !== undefined) {
            node.style.backgroundImage = `url(${previewUrl})`;
          } else if (node.attributes) {
            node.attributes.src = previewUrl;
          }
        }
        setPendingFiles(pf => ({ ...pf, [nodePath]: file }));
      }

      undoManager.current?.push(newJSON, `Replace image: ${bindingPath || nodePath}`);
      return newJSON;
    });
    setIsDirty(true);
  }, []);

  // Handle theme changes — updates current page and master theme for cross-page consistency
  const handleThemeChange = useCallback((newTheme) => {
    setTemplateJSON(prev => {
      if (!prev) return prev;
      const newJSON = { ...deepClone(prev), theme: newTheme };
      undoManager.current?.push(newJSON, 'Update theme');
      return newJSON;
    });
    setIsDirty(true);
    // Persist as master theme so other pages pick it up
    masterThemeRef.current = JSON.parse(JSON.stringify(newTheme));
  }, []);

  // Handle business info changes
  const handleBusinessInfoChange = useCallback((newContent) => {
    setTemplateJSON(prev => {
      if (!prev) return prev;
      const newJSON = deepClone(prev);
      newJSON.content = { ...newJSON.content, ...newContent };
      undoManager.current?.push(newJSON, 'Update business info');
      return newJSON;
    });
    setIsDirty(true);
  }, []);

  // Handle product picker updates — updates content.products (or other product section keys)
  const handleUpdateProducts = useCallback((sectionKey, newValue) => {
    setTemplateJSON(prev => {
      if (!prev) return prev;
      const newJSON = deepClone(prev);
      if (!newJSON.content) newJSON.content = {};
      newJSON.content[sectionKey] = newValue;
      undoManager.current?.push(newJSON, `Update ${sectionKey.replace(/_/g, ' ')}`);
      return newJSON;
    });
    setIsDirty(true);
  }, []);

  // Handle opening config panel for navbar/footer
  const handleOpenConfigPanel = useCallback((panelType) => {
    setActiveConfigPanel(panelType);
    setShowThemePanel(false);
    setShowBusinessPanel(false);
    setShowSectionLibrary(false);
    setShowVersionHistory(false);
    setShowAnimationPanel(false);
    setShowProductPicker(false);
    setShowPropertiesPanel(false);
    setSelectedElement(null);
    setEditingNodePath(null);
  }, []);

  // Handle navbar/footer attribute changes — updates the block's structure.attributes in the JSON
  // Uses functional state update to avoid stale closure issues during fast typing
  const handleComponentAttributeChange = useCallback((componentType, newAttributes) => {
    setTemplateJSON(prev => {
      if (!prev) return prev;
      const newJSON = deepClone(prev);
      for (const block of newJSON.blocks) {
        if (updateBlockAttributes(block.structure, componentType, newAttributes)) break;
      }
      undoManager.current?.push(newJSON, `Update ${componentType} settings`);
      return newJSON;
    });
    setIsDirty(true);
  }, []);

  // Handle element click (single-click = select, text = also enter edit mode)
  const handleElementClick = useCallback((info) => {
    // info: { type, domNode, nodePath, structure, tagName, bindingPath }
    setSelectedElement(info);
    setShowPropertiesPanel(true);
    // Close other panels
    setShowThemePanel(false);
    setShowBusinessPanel(false);
    setShowSectionLibrary(false);
    setShowVersionHistory(false);
    // For text elements, single-click enters edit mode immediately (matching old UX)
    if (info.type === 'text' && info.nodePath) {
      setEditingNodePath(info.nodePath);
      // Task 4.14: Push undo snapshot when entering text edit mode
      // so user can undo back to the pre-edit state
      if (templateJSONRef.current) {
        undoManager.current?.push(templateJSONRef.current, 'Before text edit');
      }
    } else {
      setEditingNodePath(null);
    }
  }, []);

  // Handle element double-click (enter inline text editing)
  const handleElementDoubleClick = useCallback((nodePath) => {
    setEditingNodePath(nodePath);
  }, []);

  // Handle element style change from PropertiesPanel
  // Uses functional state update to avoid stale closure issues
  const handleElementStyleChange = useCallback((nodePath, property, value, changeType) => {
    setTemplateJSON(prev => {
      if (!prev) return prev;

      // If it's an attribute change (like href, target)
      if (changeType === 'attribute') {
        const newJSON = deepClone(prev);
        const node = findNodeByPath(newJSON, nodePath);
        if (node) {
          if (!node.attributes) node.attributes = {};
          node.attributes[property] = value;
          undoManager.current?.push(newJSON, `Change ${property}`);
          setSelectedElement(sel => sel ? { ...sel, structure: node } : null);
          return newJSON;
        }
        return prev;
      }

      const newJSON = updateNodeStyle(prev, nodePath, property, value);
      undoManager.current?.push(newJSON, `Style: ${property}`);
      const updatedNode = findNodeByPath(newJSON, nodePath);
      if (updatedNode) {
        setSelectedElement(sel => sel ? { ...sel, structure: updatedNode } : null);
      }
      return newJSON;
    });
    setIsDirty(true);
  }, []);

  // Handle block reordering
  const handleBlockReorder = useCallback((fromIndex, toIndex) => {
    setTemplateJSON(prev => {
      if (!prev) return prev;
      const newJSON = deepClone(prev);
      const blocks = [...newJSON.blocks];
      const [moved] = blocks.splice(fromIndex, 1);
      blocks.splice(toIndex, 0, moved);
      blocks.forEach((block, i) => { block.position = i + 1; });
      newJSON.blocks = blocks;
      undoManager.current?.push(newJSON, 'Reorder blocks');
      return newJSON;
    });
    setIsDirty(true);
  }, []);

  // Handle block visibility toggle
  const handleBlockToggle = useCallback((index) => {
    setTemplateJSON(prev => {
      if (!prev) return prev;
      const newJSON = deepClone(prev);
      newJSON.blocks[index].enabled = !newJSON.blocks[index].enabled;
      undoManager.current?.push(newJSON, 'Toggle block visibility');
      return newJSON;
    });
    setIsDirty(true);
  }, []);

  // Handle block duplication
  const handleBlockDuplicate = useCallback((index) => {
    setTemplateJSON(prev => {
      if (!prev) return prev;
      const newJSON = deepClone(prev);
      const original = newJSON.blocks[index];
      const clone = deepClone(original);
      clone.id = (original.id || 'section') + '-copy-' + Date.now();
      clone.position = original.position + 1;
      newJSON.blocks.splice(index + 1, 0, clone);
      newJSON.blocks.forEach((b, i) => { b.position = i + 1; });
      undoManager.current?.push(newJSON, 'Duplicate section: ' + (original.id || index));
      return newJSON;
    });
    setIsDirty(true);
  }, []);

  // Undo/Redo — defined early so handleBlockDelete can reference them
  const handleUndo = useCallback(() => {
    const desc = undoManager.current?.getDescription() || 'last change';
    const state = undoManager.current?.undo();
    if (state) {
      setTemplateJSON(state);
      setIsDirty(true);
      setUndoToast({ action: 'Undo', description: desc });
      setTimeout(() => setUndoToast(null), 2000);
    }
  }, []);

  const handleRedo = useCallback(() => {
    const state = undoManager.current?.redo();
    if (state) {
      setTemplateJSON(state);
      setIsDirty(true);
      const desc = undoManager.current?.getDescription() || 'last change';
      setUndoToast({ action: 'Redo', description: desc });
      setTimeout(() => setUndoToast(null), 2000);
    }
  }, []);

  // Show a temporary undo toast at the bottom of the screen
  const showUndoToast = useCallback((message, undoFn) => {
    const existing = document.getElementById('vasstr-undo-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.id = 'vasstr-undo-toast';
    toast.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#1e293b;color:white;padding:10px 20px;border-radius:10px;font-size:13px;font-weight:500;display:flex;align-items:center;gap:12px;box-shadow:0 8px 24px rgba(0,0,0,0.2);z-index:99999;transition:opacity 0.3s ease;';
    toast.innerHTML = '<span>' + message + '</span><button id="vasstr-undo-btn" style="background:#d45614;color:white;border:none;padding:4px 12px;border-radius:6px;cursor:pointer;font-size:12px;font-weight:600;">Undo</button>';
    document.body.appendChild(toast);

    const undoBtn = document.getElementById('vasstr-undo-btn');
    if (undoBtn) {
      undoBtn.addEventListener('click', () => {
        if (undoFn) undoFn();
        toast.remove();
      });
    }

    setTimeout(() => {
      if (toast.parentNode) {
        toast.style.opacity = '0';
        setTimeout(() => { if (toast.parentNode) toast.remove(); }, 300);
      }
    }, 5000);
  }, []);

  // Handle block deletion — uses templateJSONRef to read label without stale closure
  const templateJSONRef = useRef(null);
  templateJSONRef.current = templateJSON;

  const handleBlockDelete = useCallback((index) => {
    const cur = templateJSONRef.current;
    if (!cur) return;
    const rawBlockId = cur.blocks[index]?.id || ('Section ' + (index + 1));
    const blockLabel = rawBlockId.charAt(0).toUpperCase() + rawBlockId.slice(1).replace(/[-_]/g, ' ');

    // Instant delete — no confirmation dialog
    setTemplateJSON(prev => {
      if (!prev) return prev;
      const newJSON = deepClone(prev);
      newJSON.blocks.splice(index, 1);
      newJSON.blocks.forEach((b, i) => { b.position = i + 1; });
      undoManager.current?.push(newJSON, 'Delete section: ' + blockLabel);
      return newJSON;
    });
    setIsDirty(true);

    // Show undo toast
    showUndoToast('"' + blockLabel + '" deleted', handleUndo);
  }, [showUndoToast, handleUndo]);

  // Track where to insert a new section (set by handleBlockAdd)
  const addAfterIndexRef = useRef(null);

  // Handle adding a new section after a given index
  const handleBlockAdd = useCallback((afterIndex) => {
    // Task 4.9: Store the target position so handleAddFromLibrary can insert there
    addAfterIndexRef.current = typeof afterIndex === 'number' ? afterIndex : null;
    setShowSectionLibrary(true);
    // Close other panels
    setShowThemePanel(false);
    setShowBusinessPanel(false);
    setShowVersionHistory(false);
  }, []);

  // Page switching — uses handleSaveRef to avoid hook ordering issues
  const handlePageChange = useCallback(async (newPage) => {
    const doSwitch = () => {
      navbarDirtyRef.current = false;
      footerDirtyRef.current = false;
      setSearchParams({ template: templateNo, page: newPage });
    };

    const hasUnsaved = isDirty || navbarDirtyRef.current || footerDirtyRef.current;

    if (hasUnsaved) {
      // Auto-save draft silently before switching — no confirmation dialog
      try {
        if (handleSaveRef.current) {
          await handleSaveRef.current({ silent: true });
        }
      } catch (err) {
        devLog.error('Auto-save before page switch failed:', err);
        // Task 4.10: On save failure, ask user before discarding
        const result = await Swal.fire({
          title: 'Save Failed',
          text: 'Your changes could not be saved. Switch page anyway? Unsaved changes will be lost.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Switch Anyway',
          cancelButtonText: 'Stay Here',
          confirmButtonColor: '#dc2626',
        });
        if (!result.isConfirmed) return; // User chose to stay
      }
    }
    doSwitch();
  }, [isDirty, templateNo, setSearchParams]);

  // (handleUndo, handleRedo, showUndoToast moved above handleBlockDelete)

  // Save navbar/footer config to backend if modified from a non-layout page
  const saveNavbarFooterIfDirty = useCallback(async () => {
    const csrfToken = Cookies.get('csrftoken');
    const headers = {
      Authorization: `Bearer ${accessToken}`,
      'X-CSRFToken': csrfToken,
      'Content-Type': 'multipart/form-data'
    };

    if (navbarDirtyRef.current && navbarAttrs) {
      try {
        const navJSON = {
          version: '2.0',
          page_type: 'navbar',
          template_id: templateNo,
          blocks: [{
            id: 'navbar',
            type: 'section',
            position: 1,
            enabled: true,
            structure: { type: 'NavbarWrapper', attributes: navbarAttrs, children: [] }
          }],
          content: {},
          theme: masterThemeRef.current || {}
        };
        const fd = new FormData();
        fd.append('temp_no', templateNo);
        fd.append('template_json', JSON.stringify(navJSON));
        await axios.post(
          `${API_BASE_URL}/api/template/${templateNo}/page/navbar/save`,
          fd,
          { headers }
        );
        navbarDirtyRef.current = false;
      } catch (err) {
        devLog.error('Failed to save navbar config:', err);
      }
    }

    if (footerDirtyRef.current && footerAttrs) {
      try {
        const footJSON = {
          version: '2.0',
          page_type: 'footer',
          template_id: templateNo,
          blocks: [{
            id: 'footer',
            type: 'section',
            position: 1,
            enabled: true,
            structure: { type: 'FooterWrapper', attributes: footerAttrs, children: [] }
          }],
          content: {},
          theme: masterThemeRef.current || {}
        };
        const fd = new FormData();
        fd.append('temp_no', templateNo);
        fd.append('template_json', JSON.stringify(footJSON));
        await axios.post(
          `${API_BASE_URL}/api/template/${templateNo}/page/footer/save`,
          fd,
          { headers }
        );
        footerDirtyRef.current = false;
      } catch (err) {
        devLog.error('Failed to save footer config:', err);
      }
    }
  }, [accessToken, templateNo, navbarAttrs, footerAttrs]);

  // Pre-publish checklist — warn on first publish only
  const runPrePublishCheck = useCallback(() => {
    const issues = [];
    const content = templateJSON?.content || {};
    const brand = content.brand || {};

    if (!brand.name || brand.name === 'Brand' || brand.name === 'BRAND') {
      issues.push('Set your brand name (click "Brand Info")');
    }
    if (!brand.logo && !brand.image) {
      issues.push('Upload your logo (click "Brand Info")');
    }

    // Check if using placeholder / stock images
    // Task 4.18: Added placehold.co to stock image detection
    const jsonStr = JSON.stringify(templateJSON);
    if (jsonStr.includes('pexels.com') || jsonStr.includes('unsplash.com') ||
        jsonStr.includes('placehold.co') || jsonStr.includes('via.placeholder.com') ||
        jsonStr.includes('picsum.photos')) {
      issues.push('Replace stock photos with your own images');
    }

    return issues;
  }, [templateJSON]);

  // Save to server
  // silent: if true, skip the success modal (used during page-switch save)
  const handleSave = useCallback(async (options = {}) => {
    const { silent = false } = typeof options === 'object' ? options : {};
    if (!templateJSON || !accessToken || isSavingRef.current) return;

    // Task 4.5: Cancel any pending auto-save when manual save begins
    if (autoSaveTimerRef.current) {
      clearInterval(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    // Pre-publish check on first publish (non-silent only)
    if (!silent) {
      const publishWarnKey = `vasstr_publish_warned_${templateNo}`;
      const alreadyWarned = localStorage.getItem(publishWarnKey);
      if (!alreadyWarned) {
        const issues = runPrePublishCheck();
        if (issues.length > 0) {
          const result = await Swal.fire({
            title: 'Quick Check Before Publishing',
            html: `
              <div style="text-align:left;padding:8px 0;">
                <p style="color:#6b7280;margin-bottom:12px;">These are optional but recommended:</p>
                ${issues.map(i => `<div style="display:flex;gap:8px;align-items:center;padding:6px 0;color:#f59e0b;font-size:14px;">&#9888;&#65039; ${i}</div>`).join('')}
              </div>
            `,
            confirmButtonText: 'Publish Anyway',
            showCancelButton: true,
            cancelButtonText: "I'll Fix These First",
            confirmButtonColor: '#d45614',
          });
          if (!result.isConfirmed) return; // User wants to fix issues first
          localStorage.setItem(publishWarnKey, 'true');
        }
      }
    }

    setIsSaving(true);
    isSavingRef.current = true;

    try {
      const submitData = new FormData();
      submitData.append('temp_no', templateNo);
      submitData.append('temp_name', templateJSON.template_name || `Template ${templateNo}`);
      submitData.append('template_json', JSON.stringify(templateJSON));

      const brand = templateJSON.content?.brand;
      if (brand?.name) submitData.append('brand_name', brand.name);

      const contact = templateJSON.content?.contact;
      if (contact?.email) submitData.append('contact_email', contact.email);
      if (contact?.phone) submitData.append('contact_phone', contact.phone);
      if (contact?.address) submitData.append('contact_address', contact.address);

      const social = templateJSON.content?.social;
      if (social) {
        if (social.instagram) submitData.append('social_instagram', social.instagram);
        if (social.facebook) submitData.append('social_facebook', social.facebook);
        if (social.twitter) submitData.append('social_twitter', social.twitter);
        if (social.youtube) submitData.append('social_youtube', social.youtube);
        if (social.linkedin) submitData.append('social_linkedin', social.linkedin);
      }

      // Append files and build a mapping of backend field names to original content paths
      const fileBindings = {};
      Object.entries(pendingFiles).forEach(([fieldPath, file]) => {
        const backendField = mapFieldToBackend(fieldPath);
        submitData.append(backendField, file);
        fileBindings[backendField] = fieldPath;
      });
      if (Object.keys(fileBindings).length > 0) {
        submitData.append('_file_bindings', JSON.stringify(fileBindings));
      }

      const csrfToken = Cookies.get('csrftoken');

      await axios.post(
        `${API_BASE_URL}/api/template/${templateNo}/page/${pageType}/save`,
        submitData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'X-CSRFToken': csrfToken,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Also save navbar/footer config if modified from a non-layout page
      const isLayoutPage = pageType === 'navbar' || pageType === 'footer';
      if (!isLayoutPage) {
        await saveNavbarFooterIfDirty();
      }

      setIsDirty(false);
      setPendingFiles({});
      // Clear local draft on save
      try {
        localStorage.removeItem(`editor_draft_${templateNo}_${pageType}`);
      } catch (e) {}

      if (silent) {
        // Silent save — just show a brief toast
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus(null), 2000);
        return;
      }

      // After save — first save gets big modal, subsequent saves get subtle toast
      const firstSaveKey = `editor_first_save_${templateNo}`;
      const isFirstSave = !localStorage.getItem(firstSaveKey);
      if (isFirstSave) localStorage.setItem(firstSaveKey, Date.now().toString());

      if (isFirstSave) {
        // Big celebratory modal for first-ever publish
        Swal.fire({
          icon: 'success',
          title: 'Your website is live!',
          html: `<p style="color:#64748b;font-size:14px;margin-top:4px">Your first design is saved and ready for your customers to see.</p>${user?.username ? `<p style="margin-top:8px"><a href="http://${user.username}.${CUSTOMER_SITE_BASE}" target="_blank" style="color:#4f46e5;font-weight:600;text-decoration:underline">View your live site</a></p>` : ''}`,
          showConfirmButton: true,
          confirmButtonText: user?.username ? 'Open My Website' : 'Continue Editing',
          confirmButtonColor: '#4f46e5',
          showDenyButton: true,
          denyButtonText: 'Back to Dashboard',
          denyButtonColor: '#111827',
          showCancelButton: true,
          cancelButtonText: 'Continue Editing',
          backdrop: 'rgba(0,0,0,0.4)',
          customClass: { popup: 'shadow-2xl rounded-2xl' },
          showClass: { popup: 'animate__animated animate__fadeInUp animate__faster' },
          hideClass: { popup: 'animate__animated animate__fadeOutDown animate__faster' },
        }).then((result) => {
          if (result.isConfirmed) {
            if (user?.username) {
              window.open(`http://${user.username}.${CUSTOMER_SITE_BASE}`, '_blank');
            }
          } else if (result.isDenied) {
            navigate(`/template-builder/${templateNo}`);
          }
        });
      } else {
        // Subtle toast for subsequent saves — no interruption
        const toast = document.createElement('div');
        toast.innerHTML = `
          <div style="
            position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
            background: linear-gradient(135deg, #065f46, #047857);
            color: white; padding: 12px 24px; border-radius: 12px;
            font-size: 14px; font-weight: 600; display: flex; align-items: center; gap: 8px;
            box-shadow: 0 8px 24px rgba(0,0,0,0.2); z-index: 99999;
            animation: toastSlideUp 0.3s ease-out;
          ">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            Changes saved &amp; published!
          </div>
        `;
        document.body.appendChild(toast);
        setTimeout(() => {
          const inner = toast.firstElementChild;
          if (inner) { inner.style.opacity = '0'; inner.style.transition = 'opacity 0.3s ease'; }
          setTimeout(() => toast.remove(), 300);
        }, 2500);
      }
    } catch (err) {
      devLog.error('Save error:', err);
      if (!silent) {
        // Detect subscription error (403 with subscription_required code)
        const isSubscriptionError = err?.response?.status === 403 &&
          (err?.response?.data?.code === 'subscription_required' || err?.response?.data?.error?.includes('Subscription'));

        if (isSubscriptionError) {
          Swal.fire({
            icon: 'warning',
            title: 'Subscription Required',
            html: '<p style="color:#64748b;font-size:14px;margin-top:4px">Your subscription has expired or is not active. Please subscribe or activate your free trial to save changes.</p>',
            confirmButtonText: 'Go to Subscription',
            confirmButtonColor: '#d45614',
            showCancelButton: true,
            cancelButtonText: 'Keep Editing',
          }).then((result) => {
            if (result.isConfirmed) navigate('/subscription');
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Unable to save your changes',
            html: '<p style="color:#64748b;font-size:14px;margin-top:4px">Don\'t worry -- your work is safely saved on this device. This can happen if your internet connection dropped or our servers are temporarily busy.</p>',
            confirmButtonText: 'Try Again',
            confirmButtonColor: '#111827',
            showCancelButton: true,
            cancelButtonText: 'Keep Editing',
          }).then((result) => {
            if (result.isConfirmed) handleSave();
          });
        }
      }
    } finally {
      setIsSaving(false);
      isSavingRef.current = false;
    }
  }, [templateJSON, accessToken, templateNo, pageType, pendingFiles, navigate, saveNavbarFooterIfDirty, runPrePublishCheck]);

  // Keep ref in sync so handlePageChange can call it without circular dependency
  handleSaveRef.current = handleSave;

  // Save draft to server (without publishing)
  const handleSaveDraft = useCallback(async () => {
    if (!templateJSON || isDraftSaving) return;
    setIsDraftSaving(true);
    try {
      const csrfToken = Cookies.get('csrftoken');
      await axios.post(
        `${API_BASE_URL}/api/template-drafts/save/`,
        {
          temp_no: templateNo,
          draft_data: templateJSON,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'X-CSRFToken': csrfToken,
          }
        }
      );
      // Also save locally
      try {
        localStorage.setItem(`editor_draft_${templateNo}_${pageType}`, JSON.stringify({
          json: templateJSON,
          timestamp: Date.now(),
        }));
      } catch (e) {}

      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus(null), 2000);
    } catch (err) {
      devLog.error('Draft save error:', err);
      // Silent fail for drafts — still save locally
      try {
        localStorage.setItem(`editor_draft_${templateNo}_${pageType}`, JSON.stringify({
          json: templateJSON,
          timestamp: Date.now(),
        }));
      } catch (e) {}
    } finally {
      setIsDraftSaving(false);
    }
  }, [templateJSON, isDraftSaving, templateNo, pageType, accessToken]);

  // Reset to original base template
  const handleResetToOriginal = useCallback(async () => {
    const result = await Swal.fire({
      title: 'Reset to Original Template?',
      text: 'This will undo ALL your changes and restore the original template design. This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Reset Everything',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
    });

    if (result.isConfirmed) {
      try {
        // Fetch the base template from static files
        const pathMap = {
          'main': `template_${templateNo}.json`,
          'about': `about/about${templateNo}.json`,
          'shop': `shop/shop${templateNo}.json`,
          'product': `product/product${templateNo}.json`,
          'contact': `contact/contact${templateNo}.json`,
          'login': `login/login${templateNo}.json`,
          'cart': `cart/cart${templateNo}.json`,
          'checkout': `checkout/checkout${templateNo}.json`,
          'signup': `signup/signup${templateNo}.json`,
          'navbar': `navbar/navbar${templateNo}.json`,
          'footer': `footer/footer${templateNo}.json`,
          'privacy': `privacy/privacy${templateNo}.json`,
          'shipping': `shipping/shipping${templateNo}.json`,
          'terms': `terms/terms${templateNo}.json`,
          'faqs': `faqs/faqs${templateNo}.json`,
          'categories': `categories/categories${templateNo}.json`,
          'collections': `collections/collections${templateNo}.json`,
        };
        const path = pathMap[pageType || 'main'] || pathMap['main'];
        let rawJSON;
        try {
          rawJSON = (await axios.get(`/static/template_bases/${path}`)).data;
        } catch {
          rawJSON = (await axios.get(`${API_BASE_URL}/static/template_bases/${path}`)).data;
        }
        if (rawJSON) {
          const baseJSON = normalizeTemplateJSON(rawJSON, pageType);
          setTemplateJSON(baseJSON);
          setIsDirty(true);

          // Task 4.12: Also reset navbar and footer configs to their base templates
          try {
            const baseNav = (await axios.get(`/static/template_bases/navbar/navbar${templateNo}.json`).catch(() =>
              axios.get(`${API_BASE_URL}/static/template_bases/navbar/navbar${templateNo}.json`)
            )).data;
            if (baseNav) {
              const navAttrs = baseNav?.structure?.attributes || baseNav?.blocks?.[0]?.structure?.attributes;
              if (navAttrs) {
                setNavbarAttrs(navAttrs);
                navbarDirtyRef.current = true;
              }
            }
          } catch { /* navbar reset optional */ }

          try {
            const baseFoot = (await axios.get(`/static/template_bases/footer/footer${templateNo}.json`).catch(() =>
              axios.get(`${API_BASE_URL}/static/template_bases/footer/footer${templateNo}.json`)
            )).data;
            if (baseFoot) {
              const footAttrs = baseFoot?.structure?.attributes || baseFoot?.blocks?.[0]?.structure?.attributes;
              if (footAttrs) {
                setFooterAttrs(footAttrs);
                footerDirtyRef.current = true;
              }
            }
          } catch { /* footer reset optional */ }

          Swal.fire('Reset Complete', 'The template has been restored to its original design.', 'success');
        }
      } catch (err) {
        Swal.fire('Error', 'Could not reset template. Please try again.', 'error');
      }
    }
  }, [templateNo, pageType, normalizeTemplateJSON]);

  // Autosave draft every 30 seconds (saves to draft, not live)
  useEffect(() => {
    if (autoSaveTimerRef.current) clearInterval(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setInterval(() => {
      if (isDirty && !isSaving && !isDraftSaving && templateJSON && accessToken) {
        setAutoSaveStatus('saving');
        handleSaveDraft().then(() => {
          setAutoSaveStatus('saved');
          setTimeout(() => setAutoSaveStatus(null), 3000);
        }).catch(() => {
          setAutoSaveStatus(null);
        });
      }
    }, 30000);
    return () => {
      if (autoSaveTimerRef.current) clearInterval(autoSaveTimerRef.current);
    };
  }, [isDirty, isSaving, isDraftSaving, handleSaveDraft, templateJSON, accessToken]);

  // Task 4.6 & 4.15: Duplicate fetch/replacement effects removed.
  // Categories and collections are already fetched in the single fetchDesignerData effect above.
  // Data replacement (categories/collections) is handled in the product data replacement effect above.

  // Toolbar actions (must be defined before keyboard shortcuts useEffect that references it)
  const handleToolbarAction = useCallback((action, value) => {
    if (!selectedElement) return;
    switch (action) {
      case 'bold':
        document.execCommand('bold', false, null);
        break;
      case 'italic':
        document.execCommand('italic', false, null);
        break;
      case 'underline':
        document.execCommand('underline', false, null);
        break;
      case 'strikethrough':
        document.execCommand('strikeThrough', false, null);
        break;
      case 'align-left':
      case 'align-center':
      case 'align-right':
        if (selectedElement.domNode) {
          selectedElement.domNode.style.textAlign = action.replace('align-', '');
        }
        break;
      case 'font-size-increase':
      case 'font-size-decrease': {
        if (selectedElement.domNode) {
          const current = parseFloat(window.getComputedStyle(selectedElement.domNode).fontSize) || 16;
          const delta = action === 'font-size-increase' ? 2 : -2;
          const newSize = Math.max(8, Math.min(120, current + delta));
          if (selectedElement.nodePath) {
            handleElementStyleChange(selectedElement.nodePath, 'fontSize', `${newSize}px`);
          }
        }
        break;
      }
      case 'text-color':
        if (value && selectedElement.nodePath) {
          handleElementStyleChange(selectedElement.nodePath, 'color', value);
        }
        break;
      case 'link':
        // Legacy: no-op (inline dropdown in FloatingToolbar now uses 'link-set')
        break;
      case 'link-set': {
        // Inline link set — value passed directly from the toolbar dropdown
        const newHref = value || '';
        if (!selectedElement?.nodePath) break;

        setTemplateJSON(prev => {
          if (!prev) return prev;
          const newJSON = deepClone(prev);
          const node = findNodeByPath(newJSON, selectedElement.nodePath);

          // Task 4.8: Detect if element is inside a loop (nodePath contains .loop.items.)
          // For looped items, the link applies to the specific loop item's node, not globally
          const isLoopItem = selectedElement.nodePath.includes('.loop.') ||
                             selectedElement.nodePath.includes('.items.');
          // If inside a loop, we still update the node directly — the loop item's
          // structure is part of the template JSON tree, so findNodeByPath works correctly.
          if (node) {
            if (newHref) {
              // === If original target was a data binding, update the content too ===
              const bindingFields = [
                node.events?.onClick?.target,
                node.events?.onClick?.path,
                node.events?.click,
                node.action?.path,
                node.action?.target,
                node.attributes?.href,
                node.href,
              ];
              for (const fieldVal of bindingFields) {
                if (typeof fieldVal === 'string' && fieldVal.includes('{{') && fieldVal.includes('}}')) {
                  const bindingMatch = fieldVal.match(/\{\{([^}]+)\}\}/);
                  if (bindingMatch) {
                    const bindingPath = bindingMatch[1].trim();
                    if (bindingPath.startsWith('content.')) {
                      const contentPath = bindingPath.substring(8);
                      if (!newJSON.content) newJSON.content = {};
                      const keys = contentPath.split('.');
                      let current = newJSON.content;
                      for (let i = 0; i < keys.length - 1; i++) {
                        if (!current[keys[i]] || typeof current[keys[i]] !== 'object') {
                          current[keys[i]] = {};
                        }
                        current = current[keys[i]];
                      }
                      current[keys[keys.length - 1]] = newHref;
                    }
                  }
                }
              }

              // === ALWAYS ensure events.onClick navigate is set ===
              if (!node.events) node.events = {};
              if (!node.events.onClick) {
                node.events.onClick = { type: 'navigate', target: newHref };
              } else {
                node.events.onClick.target = newHref;
                if (!node.events.onClick.type) node.events.onClick.type = 'navigate';
              }

              // Also update events.click if it exists (alternate string format)
              if (node.events.click) {
                if (typeof node.events.click === 'string') {
                  node.events.click = 'navigate:' + newHref;
                } else if (typeof node.events.click === 'object') {
                  node.events.click.path = newHref;
                  node.events.click.target = newHref;
                }
              }

              // Also update top-level action if it exists (templates 10-15 format)
              if (node.action) {
                node.action.path = newHref;
                node.action.target = newHref;
              }

              // Set href for <a> tags
              if (!node.attributes) node.attributes = {};
              node.attributes.href = newHref;

              // Internal links shouldn't open in new tab
              if (newHref.startsWith('/')) {
                delete node.attributes.target;
              } else if (newHref.startsWith('http')) {
                node.attributes.target = '_blank';
              }
            } else {
              // === Clear link ===
              const bindingFields = [
                node.events?.onClick?.target,
                node.events?.onClick?.path,
                node.action?.path,
                node.action?.target,
                node.attributes?.href,
                node.href,
              ];
              for (const fieldVal of bindingFields) {
                if (typeof fieldVal === 'string' && fieldVal.includes('{{') && fieldVal.includes('}}')) {
                  const bindingMatch = fieldVal.match(/\{\{([^}]+)\}\}/);
                  if (bindingMatch) {
                    const bindingPath = bindingMatch[1].trim();
                    if (bindingPath.startsWith('content.')) {
                      const contentPath = bindingPath.substring(8);
                      if (newJSON.content) {
                        const keys = contentPath.split('.');
                        let current = newJSON.content;
                        for (let i = 0; i < keys.length - 1; i++) {
                          if (!current[keys[i]] || typeof current[keys[i]] !== 'object') break;
                          current = current[keys[i]];
                        }
                        if (current && keys.length > 0) {
                          current[keys[keys.length - 1]] = '';
                        }
                      }
                    }
                  }
                }
              }

              if (node.events?.onClick?.type === 'navigate') {
                delete node.events.onClick;
                if (node.events && Object.keys(node.events).length === 0) {
                  delete node.events;
                }
              }
              if (node.events?.click) {
                if (typeof node.events.click === 'string' && node.events.click.startsWith('navigate:')) {
                  delete node.events.click;
                }
              }
              if (node.action?.type === 'navigate') {
                delete node.action;
              }
              if (node.attributes) {
                delete node.attributes.href;
                delete node.attributes.target;
              }
            }
            undoManager.current?.push(newJSON, 'Edit link');
            return newJSON;
          } else if (newHref) {
            // NODE NOT FOUND FALLBACK: Still update content CTA links
            // This handles cases where findNodeByPath fails for deeply nested elements
            devLog.warn('[link-set] Node not found at path:', selectedElement.nodePath, '- updating content CTA links directly');
            if (newJSON.content?.hero) {
              // Check if the selected element's text/position suggests it's a CTA button
              const elText = selectedElement.domNode?.textContent?.trim()?.toLowerCase() || '';
              const heroCta = newJSON.content.hero.cta_text?.toLowerCase() || '';
              const heroSecondaryCta = newJSON.content.hero.cta_secondary_text?.toLowerCase() || '';

              if (elText && heroCta && elText.includes(heroCta.substring(0, 5))) {
                newJSON.content.hero.cta_link = newHref;
                devLog.log('[link-set] Updated content.hero.cta_link to:', newHref);
              } else if (elText && heroSecondaryCta && elText.includes(heroSecondaryCta.substring(0, 5))) {
                newJSON.content.hero.cta_secondary_link = newHref;
                devLog.log('[link-set] Updated content.hero.cta_secondary_link to:', newHref);
              } else {
                // Default: update primary CTA link
                newJSON.content.hero.cta_link = newHref;
                devLog.log('[link-set] Fallback: Updated content.hero.cta_link to:', newHref);
              }
            }
            undoManager.current?.push(newJSON, 'Edit link (fallback)');
            return newJSON;
          }
          return prev;
        });
        setIsDirty(true);
        break;
      }
      case 'replace-image':
        if (selectedElement.domNode) {
          selectedElement.domNode.click();
        }
        break;
      case 'opacity':
        if (value !== undefined && selectedElement.nodePath) {
          handleElementStyleChange(selectedElement.nodePath, 'opacity', value);
        }
        break;
      case 'border-radius':
        if (value && selectedElement.nodePath) {
          handleElementStyleChange(selectedElement.nodePath, 'borderRadius', value);
        }
        break;
      case 'background-color':
        if (value && selectedElement.nodePath) {
          handleElementStyleChange(selectedElement.nodePath, 'backgroundColor', value);
        }
        break;
      case 'copy-style': {
        if (selectedElement.domNode) {
          const cs = window.getComputedStyle(selectedElement.domNode);
          setCopiedStyle({
            color: cs.color,
            backgroundColor: cs.backgroundColor,
            fontSize: cs.fontSize,
            fontWeight: cs.fontWeight,
            fontFamily: cs.fontFamily,
            lineHeight: cs.lineHeight,
            letterSpacing: cs.letterSpacing,
            textAlign: cs.textAlign,
            borderRadius: cs.borderRadius,
            opacity: cs.opacity,
            padding: cs.padding,
          });
          setUndoToast({ action: 'Copied', description: 'Style copied to clipboard' });
          setTimeout(() => setUndoToast(null), 1500);
        }
        break;
      }
      case 'paste-style': {
        if (copiedStyle && selectedElement.nodePath) {
          setTemplateJSON(prev => {
            if (!prev) return prev;
            const newJSON = deepClone(prev);
            const node = findNodeByPath(newJSON, selectedElement.nodePath);
            if (node) {
              if (!node.style) node.style = {};
              Object.entries(copiedStyle).forEach(([k, v]) => {
                if (v && v !== 'normal' && v !== 'rgba(0, 0, 0, 0)') {
                  node.style[k] = v;
                }
              });
              undoManager.current?.push(newJSON, 'Paste style');
              return newJSON;
            }
            return prev;
          });
          setIsDirty(true);
          setUndoToast({ action: 'Pasted', description: 'Style applied' });
          setTimeout(() => setUndoToast(null), 1500);
        }
        break;
      }
      case 'duplicate': {
        if (selectedElement.nodePath) {
          const pathParts = selectedElement.nodePath.split('.');
          if (pathParts.length >= 2) {
            const parentPath = pathParts.slice(0, -1).join('.');
            const childIndex = parseInt(pathParts[pathParts.length - 1], 10);
            setTemplateJSON(prev => {
              if (!prev) return prev;
              const newJSON = deepClone(prev);
              const parentNode = findNodeByPath(newJSON, parentPath);
              if (Array.isArray(parentNode) && !isNaN(childIndex) && childIndex < parentNode.length) {
                const cloned = deepClone(parentNode[childIndex]);
                parentNode.splice(childIndex + 1, 0, cloned);
                undoManager.current?.push(newJSON, 'Duplicate element');
                return newJSON;
              }
              return prev;
            });
            setIsDirty(true);
            setUndoToast({ action: 'Duplicated', description: 'Element duplicated' });
            setTimeout(() => setUndoToast(null), 1500);
          }
        }
        break;
      }
      case 'move-up': {
        if (selectedElement.nodePath) {
          const pathParts = selectedElement.nodePath.split('.');
          if (pathParts.length >= 2) {
            const parentPath = pathParts.slice(0, -1).join('.');
            const childIndex = parseInt(pathParts[pathParts.length - 1], 10);
            if (childIndex > 0) {
              setTemplateJSON(prev => {
                if (!prev) return prev;
                const newJSON = deepClone(prev);
                const parentNode = findNodeByPath(newJSON, parentPath);
                if (Array.isArray(parentNode)) {
                  const [moved] = parentNode.splice(childIndex, 1);
                  parentNode.splice(childIndex - 1, 0, moved);
                  undoManager.current?.push(newJSON, 'Move element up');
                  return newJSON;
                }
                return prev;
              });
              setIsDirty(true);
            }
          }
        }
        break;
      }
      case 'move-down': {
        if (selectedElement.nodePath) {
          const pathParts = selectedElement.nodePath.split('.');
          if (pathParts.length >= 2) {
            const parentPath = pathParts.slice(0, -1).join('.');
            const childIndex = parseInt(pathParts[pathParts.length - 1], 10);
            setTemplateJSON(prev => {
              if (!prev) return prev;
              const newJSON = deepClone(prev);
              const parentNode = findNodeByPath(newJSON, parentPath);
              if (Array.isArray(parentNode) && childIndex < parentNode.length - 1) {
                const [moved] = parentNode.splice(childIndex, 1);
                parentNode.splice(childIndex + 1, 0, moved);
                undoManager.current?.push(newJSON, 'Move element down');
                return newJSON;
              }
              return prev;
            });
            setIsDirty(true);
          }
        }
        break;
      }
      case 'delete':
        if (selectedElement.nodePath) {
          const pathParts = selectedElement.nodePath.split('.');
          if (pathParts.length >= 2) {
            const parentPath = pathParts.slice(0, -1).join('.');
            const childIndex = parseInt(pathParts[pathParts.length - 1], 10);
            setTemplateJSON(prev => {
              if (!prev) return prev;
              const newJSON = deepClone(prev);
              const parentNode = findNodeByPath(newJSON, parentPath);
              if (Array.isArray(parentNode) && !isNaN(childIndex) && childIndex < parentNode.length) {
                parentNode.splice(childIndex, 1);
                undoManager.current?.push(newJSON, 'Delete element');
                return newJSON;
              }
              return prev;
            });
            setIsDirty(true);
          }
          setSelectedElement(null);
          setEditingNodePath(null);
          setShowPropertiesPanel(false);

          // Show undo toast instead of confirmation dialog
          showUndoToast('Element deleted', handleUndo);
        }
        break;
      case 'dismiss':
        setSelectedElement(null);
        setEditingNodePath(null);
        setShowPropertiesPanel(false);
        break;
      default:
        break;
    }
  }, [selectedElement, handleElementStyleChange, copiedStyle, designerCategories, designerCollections, showUndoToast, handleUndo]);

  // Keyboard shortcuts — skip undo/redo when user is typing in a contentEditable element
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isEditing = document.activeElement?.isContentEditable;
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
        return;
      }
      // Zoom controls: Ctrl+=/+, Ctrl+-, Ctrl+0
      if ((e.ctrlKey || e.metaKey) && (e.key === '=' || e.key === '+')) {
        e.preventDefault();
        setZoomLevel(prev => Math.min(200, prev + 25));
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '-') {
        e.preventDefault();
        setZoomLevel(prev => Math.max(25, prev - 25));
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        e.preventDefault();
        setZoomLevel(100);
        return;
      }

      // Ctrl+D: duplicate selected element
      if ((e.ctrlKey || e.metaKey) && e.key === 'd' && selectedElement && !isEditing) {
        e.preventDefault();
        handleToolbarAction('duplicate');
        return;
      }

      // Escape key: exit text edit → deselect
      if (e.key === 'Escape') {
        if (editingNodePath) {
          setEditingNodePath(null);
        } else if (selectedElement) {
          setSelectedElement(null);
          setShowPropertiesPanel(false);
        }
        return;
      }

      // Delete key: remove selected element instantly with undo toast (not while editing text)
      if ((e.key === 'Delete' || e.key === 'Backspace') && !isEditing && selectedElement && !editingNodePath) {
        e.preventDefault();
        handleToolbarAction('delete');
        return;
      }

      // Don't intercept undo/redo while editing text — let the browser handle it natively
      if (isEditing) return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave, handleUndo, handleRedo, handleToolbarAction, editingNodePath, selectedElement]);

  // Ctrl+scroll wheel zoom on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const handleWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -10 : 10;
        setZoomLevel(prev => Math.max(25, Math.min(200, prev + delta)));
      }
    };
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', handleWheel);
  }, []);

  // Map frontend binding paths to backend field names
  // The backend's process_template_files_for_v2 expects specific field naming patterns
  const mapFieldToBackend = (fieldPath) => {
    // Direct content path mappings
    if (fieldPath === 'content.brand.logo') return 'brand_logo';
    if (fieldPath === 'content.hero.background_image' || fieldPath === 'content.hero.backgroundImage') return 'hero_background';
    if (fieldPath === 'content.hero.image') return 'hero_background';
    if (fieldPath === 'content.featuredImage.src') return 'featuredImage';
    if (fieldPath === 'content.philosophy.image') return 'philosophy_image';
    if (fieldPath === 'content.craftsmanship.image') return 'craftsmanship_image';
    if (fieldPath === 'content.editorial.image') return 'editorial_image';
    if (fieldPath === 'content.materials.image1') return 'materials_image1';
    if (fieldPath === 'content.materials.image2') return 'materials_image2';
    if (fieldPath === 'content.brandStory.image') return 'brandStory_image';
    if (fieldPath === 'content.brand_story.image') return 'brand_story_image';
    if (fieldPath === 'content.brandStatement.image') return 'brandStatement_image';
    if (fieldPath === 'content.mission.image') return 'mission_image';
    if (fieldPath === 'content.videoBanner.image') return 'video_banner_image';
    if (fieldPath === 'content.videoCampaign.image') return 'video_campaign_image';
    if (fieldPath === 'content.videoLookbook.image') return 'video_lookbook_image';
    if (fieldPath === 'content.theEdit.image') return 'the_edit_image';
    if (fieldPath === 'content.athlete.backgroundImage') return 'athlete_image';
    if (fieldPath === 'content.consultation.image') return 'consultation_image';
    if (fieldPath === 'content.video.image') return 'video_image';
    if (fieldPath === 'content.craftsmanship.background_image') return 'craftsmanship_bg_image';
    if (fieldPath === 'content.craftsmanship.mobileImage') return 'craftsmanship_mobile_image';
    if (fieldPath === 'content.editorial.backgroundImage') return 'editorial_bg_image';
    if (fieldPath === 'content.editorial.activeItem.image') return 'editorial_activeItem_image';
    if (fieldPath === 'content.behindScenes.image') return 'behindScenes_image';
    if (fieldPath === 'content.lookbook.featured_image') return 'lookbook_featured_image';

    // Indexed content path mappings (arrays)
    // content.categories.N.image or categories.items.N.image → category_image_N
    const categoryMatch = fieldPath.match(/^(?:content\.)?categories\.(?:items\.)?(\d+)\.image$/);
    if (categoryMatch) return `category_image_${categoryMatch[1]}`;
    // content.collections.N.image or collections.items.N.image → collection_image_N
    const collectionMatch = fieldPath.match(/^(?:content\.)?collections\.(?:items\.)?(\d+)\.image$/);
    if (collectionMatch) return `collection_image_${collectionMatch[1]}`;
    // content.gallery.N.image or gallery.items.N.image → gallery_image_N
    const galleryMatch = fieldPath.match(/^(?:content\.)?gallery\.(?:items\.)?(\d+)\.image$/);
    if (galleryMatch) return `gallery_image_${parseInt(galleryMatch[1]) + 1}`;
    // content.instagram.posts.N.image or instagram.posts.N.image → instagram_post_N
    const instagramMatch = fieldPath.match(/^(?:content\.)?instagram\.(?:posts\.)?(\d+)\.image$/);
    if (instagramMatch) return `instagram_post_${instagramMatch[1]}`;
    // content.testimonials.items.N.image or testimonials.items.N.image → testimonial_avatar_N
    const testimonialMatch = fieldPath.match(/^(?:content\.)?testimonials\.(?:items\.)?(\d+)\.image$/);
    if (testimonialMatch) return `testimonial_avatar_${testimonialMatch[1]}`;
    // content.lookbook.items.N.image or lookbook.items.N.image → lookbook_image_N
    const lookbookMatch = fieldPath.match(/^(?:content\.)?lookbook\.(?:items\.)?(\d+)\.image$/);
    if (lookbookMatch) return `lookbook_image_${lookbookMatch[1]}`;
    // content.features.items.N.image or features.items.N.image → feature_image_N
    const featureMatch = fieldPath.match(/^(?:content\.)?features\.(?:items\.)?(\d+)\.image$/);
    if (featureMatch) return `feature_image_${featureMatch[1]}`;
    // content.services.items.N.image or services.items.N.image → service_image_N
    const serviceMatch = fieldPath.match(/^(?:content\.)?services\.(?:items\.)?(\d+)\.image$/);
    if (serviceMatch) return `service_image_${serviceMatch[1]}`;
    // content.trending.items.N.image or trending.items.N.image → trending_image_N
    const trendingMatch = fieldPath.match(/^(?:content\.)?trending\.(?:items\.)?(\d+)\.image$/);
    if (trendingMatch) return `trending_image_${trendingMatch[1]}`;
    // content.portfolio.items.N.image or portfolio.items.N.image → portfolio_image_N
    const portfolioMatch = fieldPath.match(/^(?:content\.)?portfolio\.(?:items\.)?(\d+)\.image$/);
    if (portfolioMatch) return `portfolio_image_${portfolioMatch[1]}`;
    // content.designers.items.N.image or designers.items.N.image → designer_image_N
    const designerMatch = fieldPath.match(/^(?:content\.)?designers\.(?:items\.)?(\d+)\.image$/);
    if (designerMatch) return `designer_image_${designerMatch[1]}`;
    // content.cityCollections.items.N.image or cityCollections.items.N.image → city_collection_image_N
    const cityMatch = fieldPath.match(/^(?:content\.)?cityCollections\.(?:items\.)?(\d+)\.image$/);
    if (cityMatch) return `city_collection_image_${cityMatch[1]}`;
    // content.styleGuide.items.N.image or styleGuide.items.N.image → style_guide_image_N
    const styleGuideMatch = fieldPath.match(/^(?:content\.)?styleGuide\.(?:items\.)?(\d+)\.image$/);
    if (styleGuideMatch) return `style_guide_image_${styleGuideMatch[1]}`;
    // content.featuredBrands.items.N.image or featuredBrands.items.N.image → featured_brand_image_N
    const featuredBrandMatch = fieldPath.match(/^(?:content\.)?featuredBrands\.(?:items\.)?(\d+)\.image$/);
    if (featuredBrandMatch) return `featured_brand_image_${featuredBrandMatch[1]}`;
    // content.reels.items.N.image or reels.items.N.image → reel_image_N
    const reelMatch = fieldPath.match(/^(?:content\.)?reels\.(?:items\.)?(\d+)\.image$/);
    if (reelMatch) return `reel_image_${reelMatch[1]}`;
    // content.hero.collections.N.image → hero_collection_image_N
    const heroCollMatch = fieldPath.match(/^(?:content\.)?hero\.collections\.(\d+)\.image$/);
    if (heroCollMatch) return `hero_collection_image_${heroCollMatch[1]}`;
    // content.promoBanners.N.image or promoBanners.N.image → promo_banner_image_N
    const promoMatch = fieldPath.match(/^(?:content\.)?promoBanners\.(\d+)\.image$/);
    if (promoMatch) return `promo_banner_image_${promoMatch[1]}`;
    // content.intro.certifications.N.image → certification_image_N
    const certMatch = fieldPath.match(/^(?:content\.)?intro\.certifications\.(\d+)\.image$/);
    if (certMatch) return `certification_image_${certMatch[1]}`;
    // content.videos.items.N.thumbnail or videos.items.N.thumbnail → video_thumbnail_N
    const videoThumbMatch = fieldPath.match(/^(?:content\.)?videos\.(?:items\.)?(\d+)\.thumbnail$/);
    if (videoThumbMatch) return `video_thumbnail_${videoThumbMatch[1]}`;

    // Generic fallback: strip "content." prefix and replace dots with underscores
    let cleaned = fieldPath;
    if (cleaned.startsWith('content.')) cleaned = cleaned.slice(8);
    return cleaned.replace(/\./g, '_');
  };

  // Preview dimensions based on device
  const previewStyle = useMemo(() => {
    switch (previewDevice) {
      case 'mobile':
        return { width: '375px', maxWidth: '375px' };
      case 'tablet':
        return { width: '768px', maxWidth: '768px' };
      default:
        return { width: '100%', maxWidth: '100%' };
    }
  }, [previewDevice]);

  // Close panels when switching to the other
  const toggleThemePanel = useCallback(() => {
    setShowThemePanel(prev => !prev);
    if (!showThemePanel) { setShowBusinessPanel(false); setShowSectionLibrary(false); setShowVersionHistory(false); setShowAnimationPanel(false); setShowProductPicker(false); setActiveConfigPanel(null); }
  }, [showThemePanel]);

  const toggleBusinessPanel = useCallback(() => {
    setShowBusinessPanel(prev => !prev);
    if (!showBusinessPanel) { setShowThemePanel(false); setShowSectionLibrary(false); setShowVersionHistory(false); setShowAnimationPanel(false); setShowProductPicker(false); setActiveConfigPanel(null); }
  }, [showBusinessPanel]);

  const toggleSectionLibrary = useCallback(() => {
    setShowSectionLibrary(prev => !prev);
    if (!showSectionLibrary) { setShowThemePanel(false); setShowBusinessPanel(false); setShowVersionHistory(false); setShowAnimationPanel(false); setShowProductPicker(false); setActiveConfigPanel(null); }
  }, [showSectionLibrary]);

  const toggleVersionHistory = useCallback(() => {
    setShowVersionHistory(prev => !prev);
    if (!showVersionHistory) { setShowThemePanel(false); setShowBusinessPanel(false); setShowSectionLibrary(false); setShowAnimationPanel(false); setShowProductPicker(false); setActiveConfigPanel(null); }
  }, [showVersionHistory]);

  const toggleAnimationPanel = useCallback(() => {
    setShowAnimationPanel(prev => !prev);
    if (!showAnimationPanel) { setShowThemePanel(false); setShowBusinessPanel(false); setShowSectionLibrary(false); setShowVersionHistory(false); setShowProductPicker(false); setActiveConfigPanel(null); }
  }, [showAnimationPanel]);

  const toggleProductPicker = useCallback(() => {
    setShowProductPicker(prev => !prev);
    if (!showProductPicker) { setShowThemePanel(false); setShowBusinessPanel(false); setShowSectionLibrary(false); setShowVersionHistory(false); setShowAnimationPanel(false); setActiveConfigPanel(null); }
  }, [showProductPicker]);

  // Handle block animation updates from AnimationPanel
  const handleBlockAnimationUpdate = useCallback((blockIndex, updatedBlock) => {
    setTemplateJSON(prev => {
      if (!prev) return prev;
      const newJSON = deepClone(prev);
      newJSON.blocks[blockIndex] = updatedBlock;
      undoManager.current?.push(newJSON, 'Update animation: ' + (updatedBlock.id || blockIndex));
      return newJSON;
    });
    setIsDirty(true);
  }, []);

  const handleRestoreVersion = useCallback((restoredJSON) => {
    setTemplateJSON(restoredJSON);
    setIsDirty(true);
    undoManager.current?.push(restoredJSON, 'Restore from version history');
    setShowVersionHistory(false);
    Swal.fire({ icon: 'success', title: 'Version Restored', timer: 1500, showConfirmButton: false, toast: true, position: 'top-end' });
  }, []);

  const handleAddFromLibrary = useCallback((sectionJSON) => {
    setTemplateJSON(prev => {
      if (!prev) return prev;
      const newJSON = deepClone(prev);

      // Task 4.9: Insert at the stored position if available, otherwise append
      const afterIndex = addAfterIndexRef.current;
      if (afterIndex !== null && afterIndex >= 0 && afterIndex < newJSON.blocks.length) {
        sectionJSON.position = afterIndex + 2;
        newJSON.blocks.splice(afterIndex + 1, 0, sectionJSON);
        // Re-number all positions
        newJSON.blocks.forEach((b, i) => { b.position = i + 1; });
      } else {
        sectionJSON.position = newJSON.blocks.length + 1;
        newJSON.blocks.push(sectionJSON);
      }

      // Reset the insert position
      addAfterIndexRef.current = null;

      undoManager.current?.push(newJSON, 'Add section: ' + (sectionJSON.id || 'new'));
      return newJSON;
    });
    setIsDirty(true);
    // Brief toast feedback
    setUndoToast({ action: 'Added', description: (sectionJSON.id || 'section').replace(/[-_]/g, ' ').replace(/-\d+$/, '') });
    setTimeout(() => setUndoToast(null), 1500);
  }, []);

  // ── Loading state — immersive branded experience ──
  if (isLoading) {
    return (
      <div style={{
        height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#0f0f14',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Ambient glow */}
        <div style={{
          position: 'absolute', top: '30%', left: '50%', transform: 'translate(-50%, -50%)',
          width: '600px', height: '600px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%)',
          animation: 'editorPulseGlow 3s ease-in-out infinite',
        }} />
        <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
          {/* Animated logo mark */}
          <div style={{
            width: '64px', height: '64px', margin: '0 auto 28px',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute', inset: 0,
              border: '2px solid rgba(99,102,241,0.2)', borderTopColor: '#6366f1',
              borderRadius: '50%', animation: 'editorSpin 1s cubic-bezier(0.4, 0, 0.2, 1) infinite',
            }} />
            <div style={{
              position: 'absolute', inset: '8px',
              border: '2px solid rgba(168,85,247,0.15)', borderBottomColor: '#a855f7',
              borderRadius: '50%', animation: 'editorSpin 1.5s cubic-bezier(0.4, 0, 0.2, 1) infinite reverse',
            }} />
            <div style={{
              position: 'absolute', inset: '16px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Sparkles style={{ width: '20px', height: '20px', color: '#818cf8', animation: 'editorFadeInOut 2s ease-in-out infinite' }} />
            </div>
          </div>
          <p style={{
            fontSize: '18px', fontWeight: 600, color: '#e2e8f0',
            marginBottom: '8px', letterSpacing: '-0.02em',
            fontFamily: "'Inter', -apple-system, sans-serif",
          }}>
            Preparing your studio
          </p>
          <p style={{
            fontSize: '13px', color: '#64748b', fontWeight: 400,
            letterSpacing: '0.01em',
          }}>
            Your creative workspace is loading
          </p>
          {/* Progress dots */}
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', marginTop: '24px' }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: '6px', height: '6px', borderRadius: '50%',
                background: '#6366f1',
                animation: `editorDotPulse 1.2s ease-in-out ${i * 0.2}s infinite`,
              }} />
            ))}
          </div>
        </div>
        <style>{`
          @keyframes editorSpin { to { transform: rotate(360deg); } }
          @keyframes editorPulseGlow { 0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(1); } 50% { opacity: 1; transform: translate(-50%, -50%) scale(1.1); } }
          @keyframes editorFadeInOut { 0%, 100% { opacity: 0.4; } 50% { opacity: 1; } }
          @keyframes editorDotPulse { 0%, 100% { opacity: 0.3; transform: scale(1); } 50% { opacity: 1; transform: scale(1.3); } }
        `}</style>
      </div>
    );
  }

  // ── No template selected — elegant template picker ──
  if (!templateJSON) {
    const TEMPLATE_VIBES = [
      'Luxury Minimal', 'Bold Editorial', 'Warm Artisanal', 'Modern Contemporary', 'Heritage Classic',
      'Urban Streetwear', 'Bohemian Spirit', 'Couture Glamour', 'Eco Sustainable', 'Sport Luxe',
      'Resort Tropical', 'Avant-Garde', 'Ethnic Bridal', 'Scandinavian', 'Vintage Retro',
      'K-Fashion', 'Denim Workwear', 'Evening Cocktail', 'Kids Fashion', 'Accessories',
      'Haute Couture', 'Indie Designer', 'Athleisure', 'Gothic Chic', 'Pastel Dreams',
    ];
    const TEMPLATE_GRADIENTS = [
      ['#1a1a2e', '#16213e'], ['#0d0d0d', '#1a1a1a'], ['#2c1810', '#3d2415'], ['#0f172a', '#1e293b'], ['#1c1917', '#292524'],
      ['#18181b', '#27272a'], ['#431407', '#7c2d12'], ['#1e1b4b', '#312e81'], ['#064e3b', '#065f46'], ['#0c0a09', '#1c1917'],
      ['#134e4a', '#115e59'], ['#171717', '#262626'], ['#4c0519', '#881337'], ['#f8fafc', '#e2e8f0'], ['#44403c', '#57534e'],
      ['#fdf2f8', '#fce7f3'], ['#292524', '#44403c'], ['#1e1b4b', '#4c1d95'], ['#fef3c7', '#fde68a'], ['#1c1917', '#292524'],
      ['#4a0e4e', '#2d0a4e'], ['#1e3a3a', '#2a5454'], ['#2d2d2d', '#4a4a4a'], ['#1a0a2e', '#0d0d1a'], ['#fce4ec', '#f8bbd0'],
    ];
    return (
      <div style={{ height: '100vh', background: '#0f0f14', display: 'flex', flexDirection: 'column' }}>
        <div style={{
          height: '64px', background: 'rgba(15,15,20,0.95)', borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'center', padding: '0 24px', gap: '16px',
          backdropFilter: 'blur(12px)',
        }}>
          <button
            onClick={() => navigate('/TemplateThemes')}
            style={{
              width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)',
              cursor: 'pointer', color: '#94a3b8', transition: 'all 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#e2e8f0'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = '#94a3b8'; }}
          >
            <ArrowLeft style={{ width: '16px', height: '16px' }} />
          </button>
          <div>
            <span style={{ fontSize: '15px', fontWeight: 600, color: '#f1f5f9', letterSpacing: '-0.01em' }}>Choose Your Template</span>
            <p style={{ fontSize: '12px', color: '#64748b', marginTop: '1px' }}>Each template is fully customizable</p>
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '40px 32px' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '20px' }}>
              {Array.from({ length: 25 }, (_, i) => i + 1).map(num => (
                <button
                  key={num}
                  onClick={() => setSearchParams({ template: num, page: 'main' })}
                  style={{
                    background: 'rgba(255,255,255,0.03)', borderRadius: '16px',
                    border: '1px solid rgba(255,255,255,0.06)',
                    padding: '0', cursor: 'pointer', textAlign: 'left',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    overflow: 'hidden',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = 'rgba(99,102,241,0.3)';
                    e.currentTarget.style.boxShadow = '0 8px 32px rgba(99,102,241,0.15)';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{
                    aspectRatio: '4/5',
                    background: `linear-gradient(135deg, ${TEMPLATE_GRADIENTS[num - 1]?.[0] || '#1a1a2e'}, ${TEMPLATE_GRADIENTS[num - 1]?.[1] || '#16213e'})`,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    position: 'relative', overflow: 'hidden',
                  }}>
                    {/* Decorative grid pattern */}
                    <div style={{
                      position: 'absolute', inset: 0, opacity: 0.05,
                      backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
                      backgroundSize: '20px 20px',
                    }} />
                    <span style={{
                      fontSize: '36px', fontWeight: 700, color: 'rgba(255,255,255,0.15)',
                      fontFamily: "'Playfair Display', Georgia, serif",
                      letterSpacing: '-0.03em',
                    }}>{String(num).padStart(2, '0')}</span>
                  </div>
                  <div style={{ padding: '14px 16px' }}>
                    <p style={{
                      fontSize: '13px', fontWeight: 600, color: '#e2e8f0',
                      letterSpacing: '-0.01em', marginBottom: '2px',
                    }}>
                      {TEMPLATE_VIBES[num - 1] || `Template ${num}`}
                    </p>
                    <p style={{ fontSize: '11px', color: '#64748b' }}>Template {num}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Shared renderer props ──
  const rendererProps = {
    templateJSON,
    onContentChange: handleContentChange,
    onImageReplace: handleImageReplace,
    onBlockReorder: handleBlockReorder,
    onBlockToggle: handleBlockToggle,
    onBlockDuplicate: handleBlockDuplicate,
    onBlockDelete: handleBlockDelete,
    onBlockAdd: handleBlockAdd,
    editMode,
    previewDevice,
    imageBaseUrl: API_BASE_URL,
    onElementSelect: setSelectedElement,
    selectedNodePath: selectedElement?.nodePath || null,
    editingNodePath,
    onElementClick: handleElementClick,
    onElementDoubleClick: handleElementDoubleClick,
    onOpenConfigPanel: handleOpenConfigPanel,
    navbarData: navbarAttrs,
    footerData: footerAttrs,
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#e8eaed' }}>
      {/* ── Top Bar ── */}
      <EditorTopBar
        templateName={templateName}
        canUndo={undoInfo.canUndo}
        canRedo={undoInfo.canRedo}
        onUndo={handleUndo}
        onRedo={handleRedo}
        onSave={handleSave}
        onSaveDraft={handleSaveDraft}
        isSaving={isSaving}
        isDraftSaving={isDraftSaving}
        isDirty={isDirty}
        editMode={editMode}
        onToggleEditMode={() => setEditMode(!editMode)}
        previewDevice={previewDevice}
        onSetDevice={(device) => { setPreviewDevice(device); setZoomLevel(100); }}
        zoomLevel={zoomLevel}
        onZoomChange={setZoomLevel}
        showThemePanel={showThemePanel}
        onToggleThemePanel={toggleThemePanel}
        showBusinessPanel={showBusinessPanel}
        onToggleBusinessPanel={toggleBusinessPanel}
        showSectionLibrary={showSectionLibrary}
        onToggleSectionLibrary={toggleSectionLibrary}
        showVersionHistory={showVersionHistory}
        onToggleVersionHistory={toggleVersionHistory}
        showAnimationPanel={showAnimationPanel}
        onToggleAnimationPanel={toggleAnimationPanel}
        showProductPicker={showProductPicker}
        onToggleProductPicker={toggleProductPicker}
        productCount={designerProducts.length}
        onBack={() => navigate('/template-builder/' + templateNo)}
        onPreviewLive={user?.username ? () => window.open(`http://${user.username}.${CUSTOMER_SITE_BASE}`, '_blank') : undefined}
        currentPage={pageType}
        onPageChange={handlePageChange}
        autoSaveStatus={autoSaveStatus}
        onReset={handleResetToOriginal}
      />

      {/* ── Contextual hints — elegant floating bar ── */}
      {editMode && !hintsDismissed && (
        <div style={{
          background: 'linear-gradient(90deg, #faf5ff 0%, #eff6ff 50%, #f0fdf4 100%)',
          borderBottom: '1px solid rgba(99,102,241,0.08)',
          padding: '7px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '28px', flexShrink: 0, animation: 'hintSlideIn 0.4s ease-out',
        }}>
          <span style={{
            fontSize: '11px', fontWeight: 600, color: '#6366f1',
            letterSpacing: '0.04em', textTransform: 'uppercase', marginRight: '4px',
          }}>Quick tips</span>
          {[
            { icon: '✎', text: 'Click any text to edit it', color: '#7c3aed' },
            { icon: '🖼', text: 'Hover over images to replace them', color: '#2563eb' },
            { icon: '↕', text: 'Drag sections to reorder them', color: '#059669' },
            { icon: navigator.platform?.includes('Mac') ? '⌘S' : 'Ctrl+S', text: 'Save anytime', color: '#64748b' },
          ].map((hint, i) => (
            <span key={i} style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              fontSize: '11.5px', color: '#475569', fontWeight: 450,
            }}>
              <span style={{ fontSize: '12px', color: hint.color, opacity: 0.7 }}>{hint.icon}</span>
              {hint.text}
            </span>
          ))}
          <button
            onClick={() => { localStorage.setItem('editor_hints_dismissed', 'true'); setHintsDismissed(true); }}
            style={{
              background: 'rgba(0,0,0,0.04)', border: 'none', cursor: 'pointer',
              color: '#94a3b8', fontSize: '14px', lineHeight: 1, padding: '3px 8px', borderRadius: '6px',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#475569'; e.currentTarget.style.background = 'rgba(0,0,0,0.08)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
            title="Got it"
          >&times;</button>
        </div>
      )}

      {/* ── Breadcrumb bar — shows element hierarchy when selected ── */}
      {/* Wrapper div stops mousedown propagation so FloatingToolbar dismiss handler doesn't fire */}
      {editMode && selectedElement && (
        <div onMouseDown={(e) => e.stopPropagation()}>
          <BreadcrumbBar
            selectedElement={selectedElement}
            templateJSON={templateJSON}
            onSelectAncestor={(ancestorPath) => {
              const ancestorNode = findNodeByPath(templateJSON, ancestorPath);
              if (ancestorNode) {
                setSelectedElement(prev => ({
                  ...prev,
                  nodePath: ancestorPath,
                  structure: ancestorNode,
                  type: 'element',
                }));
                setEditingNodePath(null);
              }
            }}
            onDeselect={() => {
              setSelectedElement(null);
              setEditingNodePath(null);
              setShowPropertiesPanel(false);
            }}
          />
        </div>
      )}

      {/* ── Main editor area ── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ── Canvas ── */}
        <div
          ref={canvasRef}
          onClick={(e) => {
            // Click on canvas background (not on any element) → deselect
            if (e.target === e.currentTarget || e.target === canvasRef.current) {
              setSelectedElement(null);
              setEditingNodePath(null);
              setShowPropertiesPanel(false);
            }
          }}
          style={{
            flex: 1, overflow: 'auto', position: 'relative',
            transition: 'background 0.3s ease',
            background: previewDevice !== 'desktop'
              ? '#e2e4e8'
              : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
            backgroundImage: previewDevice !== 'desktop'
              ? 'radial-gradient(circle, #c8cad0 0.6px, transparent 0.6px)'
              : 'none',
            backgroundSize: previewDevice !== 'desktop' ? '20px 20px' : undefined,
          }}
        >
          <div style={{
            maxWidth: previewStyle.maxWidth, width: previewStyle.width,
            margin: '0 auto', transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            padding: previewDevice !== 'desktop' ? '24px 0' : '0',
            zoom: zoomLevel !== 100 ? zoomLevel / 100 : undefined,
          }}>

            {/* ── Device frame (mobile/tablet) ── */}
            {previewDevice !== 'desktop' && (
              <div style={{ width: previewStyle.width, margin: '0 auto', transition: 'all 0.5s ease' }}>
                {/* Device label */}
                <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                  <span style={{
                    fontSize: '11px', color: '#71717a', fontWeight: 500, letterSpacing: '0.03em',
                    background: 'rgba(255,255,255,0.8)', padding: '4px 14px', borderRadius: '8px',
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                    textTransform: 'uppercase',
                  }}>
                    {previewDevice === 'mobile' ? '375 × 812 — iPhone' : '768 × 1024 — iPad'}
                  </span>
                </div>
                {/* Status bar */}
                <div style={{
                  background: '#18181b', borderRadius: '24px 24px 0 0',
                  padding: '10px 24px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  boxShadow: '0 -2px 20px rgba(0,0,0,0.15)',
                }}>
                  <span style={{ fontSize: '12px', color: '#a1a1aa', fontWeight: 600 }}>9:41</span>
                  <div style={{ width: previewDevice === 'mobile' ? '80px' : '100px', height: '26px', background: '#09090b', borderRadius: '13px' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: '15px', height: '11px', border: '1.5px solid #71717a', borderRadius: '2.5px', position: 'relative' }}>
                      <div style={{ position: 'absolute', inset: '2px', background: '#a1a1aa', borderRadius: '1px' }} />
                    </div>
                  </div>
                </div>
                {/* Content area */}
                <div
                  className="device-preview-scroll"
                  style={{
                    background: '#ffffff',
                    overflow: 'hidden', overflowY: 'auto',
                    borderLeft: '2.5px solid #18181b', borderRight: '2.5px solid #18181b',
                    maxHeight: previewDevice === 'mobile' ? '812px' : '1024px',
                    scrollBehavior: 'smooth', scrollbarWidth: 'none', msOverflowStyle: 'none',
                    boxShadow: '0 0 40px rgba(0,0,0,0.12)',
                  }}
                >
                  <EditableRenderer {...rendererProps} />
                </div>
                {/* Home indicator */}
                <div style={{
                  background: '#18181b', borderRadius: '0 0 24px 24px',
                  padding: previewDevice === 'mobile' ? '8px 0 10px' : '12px 0 14px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 20px rgba(0,0,0,0.15)',
                }}>
                  <div style={{
                    width: previewDevice === 'mobile' ? '100px' : '40px', height: '4px',
                    background: '#52525b', borderRadius: '2px',
                  }} />
                </div>
              </div>
            )}

            {/* ── Device preview notice ── */}
            {previewDevice !== 'desktop' && (
              <div style={{
                position: 'absolute', bottom: '80px', left: '50%', transform: 'translateX(-50%)',
                backgroundColor: '#1e293b', color: 'white', padding: '8px 16px', borderRadius: '8px',
                fontSize: '12px', fontWeight: 500, zIndex: 50, display: 'flex', alignItems: 'center', gap: '6px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)', maxWidth: '420px', textAlign: 'center',
              }}>
                {previewDevice === 'mobile' ? (
                  <span>Mobile Preview — Style changes apply to all devices</span>
                ) : (
                  <span>Tablet Preview — Style changes apply to all devices</span>
                )}
                <span style={{ marginLeft: '4px', display: 'inline-flex', alignItems: 'center' }} title="The visual editor does not support per-device styles. Any style changes you make here will appear on desktop, tablet, and mobile.">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                </span>
              </div>
            )}

            {/* ── Desktop canvas ── */}
            {previewDevice === 'desktop' && (
              <div style={{
                background: '#ffffff', minHeight: '600px',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.03), 0 4px 20px rgba(0,0,0,0.08), 0 12px 40px rgba(0,0,0,0.04)',
              }}>
                {(!templateJSON?.blocks || templateJSON.blocks.length === 0) ? (
                  <div style={{
                    minHeight: '600px', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', padding: '60px 24px',
                    background: 'linear-gradient(180deg, #fafbfc 0%, #f1f3f5 100%)',
                  }}>
                    <div style={{
                      width: '80px', height: '80px', borderRadius: '20px',
                      background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginBottom: '24px', boxShadow: '0 4px 16px rgba(99,102,241,0.1)',
                    }}>
                      <Sparkles style={{ width: '32px', height: '32px', color: '#6366f1' }} />
                    </div>
                    <h2 style={{
                      fontSize: '24px', fontWeight: 700, color: '#1e293b',
                      marginBottom: '8px', letterSpacing: '-0.02em',
                      fontFamily: "'Inter', -apple-system, sans-serif",
                    }}>
                      {pageType === 'about' ? 'Start Building Your Story'
                        : pageType === 'main' ? 'Design Your Home Page'
                        : `Design Your ${({shop:'Shop',product:'Product',cart:'Cart',checkout:'Checkout',contact:'Contact',login:'Login',signup:'Signup',navbar:'Navigation',footer:'Footer',privacy:'Privacy',shipping:'Shipping',terms:'Terms',faqs:'FAQ',auth:'Login'}[pageType] || pageType)} Page`}
                    </h2>
                    <p style={{
                      fontSize: '14px', color: '#64748b', maxWidth: '400px',
                      textAlign: 'center', lineHeight: 1.6, marginBottom: '32px',
                    }}>
                      {pageType === 'about'
                        ? 'Add sections from the library to create your about page. Choose from heroes, stories, team grids, timelines, and more.'
                        : 'This page is empty. Click the button below to add ready-made sections, or use the Sections panel in the toolbar above.'}
                    </p>
                    <button
                      onClick={() => setShowSectionLibrary(true)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '12px 28px', borderRadius: '12px',
                        background: '#6366f1', color: '#ffffff',
                        fontSize: '14px', fontWeight: 600, border: 'none',
                        cursor: 'pointer', transition: 'all 0.2s',
                        boxShadow: '0 2px 8px rgba(99,102,241,0.3)',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#4f46e5'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#6366f1'; e.currentTarget.style.transform = 'translateY(0)'; }}
                    >
                      <Sparkles style={{ width: '16px', height: '16px' }} />
                      Add Sections
                    </button>
                    {pageType === 'about' && (
                      <div style={{
                        marginTop: '48px', display: 'flex', gap: '24px',
                        color: '#94a3b8', fontSize: '12px',
                      }}>
                        {['Hero', 'Story', 'Team', 'Stats', 'Gallery', 'CTA'].map(cat => (
                          <span key={cat} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#c7d2fe', display: 'inline-block' }} />
                            {cat}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <EditableRenderer {...rendererProps} />
                )}
              </div>
            )}
          </div>

          {editMode && selectedElement && (
            <FloatingToolbar
              selectedElement={selectedElement}
              onAction={handleToolbarAction}
              containerRef={canvasRef}
              copiedStyle={copiedStyle}
            />
          )}
        </div>

        {/* ── Right panels ── */}
        {[
          { show: showPropertiesPanel && selectedElement, Panel: PropertiesPanel, props: {
            selectedElement,
            themeColors: templateJSON.theme?.colors,
            onChange: handleElementStyleChange,
            onClose: () => { setShowPropertiesPanel(false); setSelectedElement(null); setEditingNodePath(null); }
          }},
          { show: showThemePanel, Panel: ThemePanel, props: { theme: templateJSON.theme, onChange: handleThemeChange, onClose: () => setShowThemePanel(false) } },
          { show: showBusinessPanel, Panel: BusinessInfoPanel, props: { content: templateJSON.content, onChange: handleBusinessInfoChange, onClose: () => setShowBusinessPanel(false) } },
          { show: showSectionLibrary, Panel: SectionLibrary, props: { templateJSON, onAddSection: handleAddFromLibrary, onClose: () => setShowSectionLibrary(false), pageType } },
          { show: showVersionHistory, Panel: VersionHistory, props: { templateNo, accessToken, apiBaseUrl: API_BASE_URL, onRestore: handleRestoreVersion, onClose: () => setShowVersionHistory(false) } },
          { show: showAnimationPanel, Panel: AnimationPanel, props: { blocks: templateJSON?.blocks || [], onUpdateBlock: handleBlockAnimationUpdate, onClose: () => setShowAnimationPanel(false) } },
          { show: showProductPicker, Panel: ProductPickerPanel, props: {
            designerProducts,
            designerCollections,
            designerCategories,
            templateJSON,
            onUpdateProducts: handleUpdateProducts,
            onClose: () => setShowProductPicker(false),
            imageBaseUrl: API_BASE_URL,
          }},
          { show: activeConfigPanel === 'navbar', Panel: NavbarConfigPanel, props: {
            attributes: pageType === 'navbar'
              ? findComponentAttributes(templateJSON, 'NavbarWrapper')
              : (navbarAttrs || {}),
            onAttributeChange: pageType === 'navbar'
              ? (newAttrs) => handleComponentAttributeChange('NavbarWrapper', newAttrs)
              : (newAttrs) => { setNavbarAttrs(prev => ({ ...prev, ...newAttrs })); navbarDirtyRef.current = true; setIsDirty(true); },
            onClose: () => setActiveConfigPanel(null),
          }},
          { show: activeConfigPanel === 'footer', Panel: FooterConfigPanel, props: {
            attributes: pageType === 'footer'
              ? findComponentAttributes(templateJSON, 'FooterWrapper')
              : (footerAttrs || {}),
            onChange: pageType === 'footer'
              ? (newAttrs) => handleComponentAttributeChange('FooterWrapper', newAttrs)
              : (newAttrs) => { setFooterAttrs(prev => ({ ...prev, ...newAttrs })); footerDirtyRef.current = true; setIsDirty(true); },
            onClose: () => setActiveConfigPanel(null),
          }},
        ].map(({ show, Panel, props }, i) => (
          <div
            key={i}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              width: show ? '320px' : '0px', opacity: show ? 1 : 0,
              transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              overflow: 'hidden', flexShrink: 0,
              transform: show ? 'translateX(0)' : 'translateX(12px)',
              boxShadow: show ? '-4px 0 20px rgba(0,0,0,0.05)' : 'none',
              borderLeft: show ? '1px solid rgba(0,0,0,0.06)' : 'none',
            }}
          >
            {show && <Panel {...props} />}
          </div>
        ))}
      </div>

      {/* ── Undo/Redo toast — minimal, non-intrusive ── */}
      {undoToast && (
        <div style={{
          position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
          zIndex: 100, background: 'rgba(15, 23, 42, 0.92)', color: '#fff',
          padding: '10px 18px',
          borderRadius: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.05)',
          fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px',
          animation: 'toastIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
          backdropFilter: 'blur(16px)', fontFamily: "'Inter', -apple-system, sans-serif",
          letterSpacing: '0.01em',
        }}>
          <span style={{
            fontWeight: 600, color: undoToast.action === 'Undo' ? '#93c5fd' : '#86efac',
            fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>{undoToast.action}</span>
          <span style={{ width: '1px', height: '12px', background: 'rgba(255,255,255,0.1)' }} />
          <span style={{ opacity: 0.6, fontWeight: 400 }}>{undoToast.description}</span>
        </div>
      )}

      {/* ── Onboarding Tour ── */}
      <OnboardingTour onComplete={() => {}} />

      {/* ── Global styles ── */}
      <style>{`
        .device-preview-scroll::-webkit-scrollbar { display: none; }
        .device-preview-scroll { scrollbar-width: none; -ms-overflow-style: none; }
        /* Smooth zoom transitions */
        .zoom-container { transition: zoom 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
        @keyframes hintSlideIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes toastIn { from { opacity: 0; transform: translate(-50%, 12px); } to { opacity: 1; transform: translate(-50%, 0); } }
        @keyframes toastSlideUp { from { transform: translateX(-50%) translateY(20px); opacity: 0; } to { transform: translateX(-50%) translateY(0); opacity: 1; } }

        /* Tooltip fade-in from below animation */
        [title]:hover::after { animation: tooltipFadeIn 0.2s ease-out; }
        @keyframes tooltipFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }

        /* Smooth scrollbar for the canvas area */
        div::-webkit-scrollbar { width: 7px; height: 7px; }
        div::-webkit-scrollbar-track { background: transparent; }
        div::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.1); border-radius: 10px; border: 2px solid transparent; background-clip: padding-box; }
        div::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.18); border: 2px solid transparent; background-clip: padding-box; }

        /* Selection highlight */
        [data-selected="true"] { outline: 2px solid #6366f1 !important; outline-offset: 2px; border-radius: 2px; }

        /* Focus ring for accessibility */
        button:focus-visible { outline: 2px solid #6366f1; outline-offset: 2px; }

        /* Smooth transitions for panel open/close */
        * { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }

        /* Editor section hover glow */
        .editor-section-animated:hover { transition: all 0.3s ease; }
      `}</style>
    </div>
  );
};

export default VisualTemplateEditor;
